import {
  OutreachContactIdsByRole,
  AgenticDecision,
  AgenticMatch,
  BuildingCandidate,
  ContactCandidates,
  ContactEnrichments,
  HubSpotMatches,
  LeadPipelineStatus,
  EpcMatch,
  TenantMatch,
  GeocodeAttempt,
  HmlrMatch,
  LeadValue,
  ProximityContext,
  SelectedOrg,
  TenantCommercial,
  useLandlordResearchTrace,
  useOrganisationList,
} from "@inrange/building-manager-api-client";
import { AITraceViewer } from "@inrange/theme-components";
import AiResearchResult from "./AiResearchResult";
import CollapsibleCell from "./CollapsibleCell";
import { WithTooltip } from "../../components/WithTooltip";
import { useOutreachPromoteGate } from "./useOutreachPromoteGate";
import {
  leadValueBadgeProps,
  leadValueLabel,
  leadValueTooltip,
} from "./leadValueDisplay";
import { ProximityChip } from "./leadProximity";
import {
  adjudicationDisplay,
  agenticDisplayName,
  confColour,
  noSelectionOptionsText,
  recommendedCandidate,
  scrubResearchJargon,
  shortReason,
  verdictDisplay,
} from "./leadResearchDisplay";
import { ContactDetailsCell, ContactsRoleCell } from "./PeopleDiscoveryPanel";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { flushSync } from "react-dom";
import { Alert, Badge, Button, Form, Modal, Table } from "react-bootstrap";
import { Link, useBlocker } from "react-router-dom";
import { v4 as uuidv4 } from "uuid";
import {
  AddressVerdict,
  humanizeComponentType,
  humanizeConfirmation,
  humanizeGranularity,
} from "./addressVerdict";
import {
  BASIC_ENRICHMENT_SERVICES,
  PREMIUM_ENRICHMENT_SERVICES,
} from "./enrichmentCopy";
import {
  bboxesOverlap,
  haversineMetres,
  polygonBbox,
  polygonsOverlap,
} from "./leadGeo";
import {
  MAX_BUILDING_DISTANCE_M,
  distanceToPolygonMetres,
  matchConfidence,
} from "./buildingMatchConfidence";
import {
  INITIAL_MAP_ZOOM,
  LeadPanelMap,
  MapViewportRequest,
} from "./LeadPanelMap";
import { buildingColourForIndex } from "./buildingPalette";
import { TenantCommercialSummary } from "./TenantCommercialSummary";
import { TenantDomainPanel } from "./TenantDomainPanel";
import { useLeadAutosave } from "./useLeadAutosave";
import { formatRelativeTime } from "@inrange/utils";
import { formatAddressCasing } from "./addressCasing";
import { PanelReviewStatus } from "./candidateStatus";
import { SaveStatusIcon } from "./SaveStatusIcon";
import { StatusBadge } from "./StatusBadge";
import {
  companyFromDecision,
  isAutoPickable,
  isLandlordAutoPickable,
} from "./leadDisplay";
import { SortHeader } from "./SortHeader";
import { SortDirection } from "./sortOptions";

const formatBuildingMatchTooltip = (b: {
  confidence: number | null;
  distanceMetres: number;
  surfaceAreaSqm: number | null;
}): string => {
  const conf = b.confidence == null ? "—" : `${Math.round(b.confidence)}%`;
  const distLine = isFinite(b.distanceMetres)
    ? `Distance to polygon: ${Math.round(b.distanceMetres)}m (factor falls off above ${MAX_BUILDING_DISTANCE_M}m)`
    : "Distance to polygon: unknown";
  const areaLine =
    b.surfaceAreaSqm != null
      ? `OSM polygon area: ${Math.round(b.surfaceAreaSqm).toLocaleString()} m²`
      : "OSM polygon area: unknown";
  return [
    `Match: ${conf} (0.6 × distance factor + 0.4 × area factor)`,
    distLine,
    areaLine,
  ].join("\n");
};

const formatGeocoderTooltip = (a: {
  source: string;
  confidenceLabel: string | null;
}): string => {
  const labelDesc =
    a.confidenceLabel &&
    GEOCODER_TOOLTIP_LABELS[a.confidenceLabel.toUpperCase()];
  const lines = [`Source: ${a.source}`];
  if (a.confidenceLabel) {
    lines.push(
      `Confidence: ${a.confidenceLabel}${labelDesc ? ` (${labelDesc})` : ""}`
    );
  }
  return lines.join("\n");
};

const GEOCODER_TOOLTIP_LABELS: Record<string, string> = {
  HIGH: "full address match (street + number)",
  MEDIUM: "street- or postcode-level match",
  LOW: "broader feature (locality / region)",
  POSTCODE_CENTROID: "postcode centroid (Mapbox didn't pin the address)",
};

const formatOrgRowTooltip = (r: {
  source: SelectedOrg["source"];
  confidence: number | null;
  detail: string;
  reasoning?: string | null;
  agenticCorroborated?: boolean;
}): string => {
  const conf = r.confidence == null ? "-" : `${Math.round(r.confidence)}%`;
  const sourceLine = (() => {
    switch (r.source) {
      case "hmlr":
        return "HMLR title deed - confidence reflects deed centroid distance to the building polygon (closer + polygon overlap = higher).";
      case "ch":
        return "Companies House - postcode-keyed search; ranked by SIC-code match, active status, address fuzziness.";
      case "osm":
        return "OpenStreetMap (Overpass) - tagged-business proximity to the building polygon.";
      case "google_places":
        return "Google Places nearby search - ranked by distance + name similarity.";
      case "platform":
        return "Existing platform Organisation - manual or fuzzy-matched override.";
      case "manual":
        return "Manually-added organisation.";
      case "agentic":
        // No generic per-source blurb for agentic rows: it was identical on
        // every candidate and just pushed the useful "Why this score" reasoning
        // (below) down. The reasoning line carries the meaning.
        return "";
      default:
        return "";
    }
  })();
  // For a native agentic row the per-candidate reasoning IS the explanation of
  // why this specific score differs from a sibling candidate's (e.g. 42% vs
  // 30%), so surface it on the confidence hover. For a corroborated non-agentic
  // row the confidence shown is that row's own (e.g. the HMLR geometric score),
  // NOT the agent's - so don't label the agent's reasoning "why this score";
  // present it as the agent's corroborating finding instead.
  const reasoningLine = (() => {
    if (r.source === "agentic") {
      return r.reasoning ? `Why this score: ${r.reasoning}` : "";
    }
    if (r.agenticCorroborated) {
      return r.reasoning
        ? `AI research corroborates this owner: ${r.reasoning}`
        : "Independently corroborated by AI research.";
    }
    return "";
  })();
  return [`Confidence: ${conf}`, sourceLine, reasoningLine, r.detail]
    .filter(Boolean)
    .join("\n");
};

const googleMapsLink = (lat: number, lon: number): string =>
  `https://www.google.com/maps/@${lat},${lon},19z/data=!3m1!1e3`;

export interface LeadEditPanelProps {
  // Subject of editing
  address: string | null;
  buildingCandidates: BuildingCandidate[];
  hmlrMatches: HmlrMatch[];
  tenantMatches: TenantMatch[];
  // Agentic landlord-research candidates. Rendered in the Landlord section
  // as a distinct source; never auto-picked. Empty until the operator runs
  // "Research landlord" on the row.
  agenticMatches?: AgenticMatch[];
  // Size + UK footprint of the row's tenant, and the lead's value band. Both
  // null until the commercial enrichment pass has run for that tenant, which
  // the tenant block renders as an explicit "Unknown" rather than a blank.
  tenantCommercial?: TenantCommercial | null;
  leadValue?: LeadValue | null;

  // Initial selections (fed in from VOA row OR Lead row)
  initialBuildingBkey: string | null;
  initialLandlord: SelectedOrg | null;
  initialTenant: SelectedOrg | null;
  initialOwnerOccupier: boolean;
  // The operator's own "this roof already has solar" flag. Off by default and only
  // ever set by hand - automatic detection is separate, future work.
  initialHasPv: boolean;
  // Existing solar (kWp) declared on the lead's linked site's Demand tab, when there
  // is one and it is non-zero. This is the DERIVED half of the same signal: the
  // outreach drafter ORs it with the flag above, so when it is set the toggle has to
  // render on (and read-only - the site is where that number is corrected), or the
  // panel would show "no existing solar" while the email says the opposite.
  // Required, not optional: an implicit default here silently reads as "not derived",
  // which is precisely the disagreement it exists to prevent. Explicitly null on the
  // search pane, where no site exists yet.
  siteExistingPvKwp: number | null;

  // Fallback geocode if no buildings
  fallbackLat: number | null;
  fallbackLon: number | null;

  // Tags on the underlying Lead (when this row is already a lead).
  // When set + non-empty, the header renders the tags below the
  // address instead of the postcode. Empty / null falls back to
  // postcode. Same on /leads (always populated from lead.tags) and
  // /leads/search (populated for rows the operator just promoted in
  // this session or that were already Leads pre-search).
  leadTags?: string[];

  // The lead's proximity anchor (the operational site the search was anchored on
  // when it was promoted), rendered as a "near <site>" chip under the header. Only
  // populated on the /leads inbox (a promoted lead has it); a not-yet-promoted
  // search candidate has none.
  proximityContext?: ProximityContext | null;

  // The row this candidate duplicates (same resolved building, ranked higher in
  // the results list), or null. Renders the same "(D)" mark the results table
  // shows so both surfaces flag the same rows.
  duplicateOfAddress?: string | null;

  // Input data the resolver was matching against. Surfaced above the
  // Buildings table so the operator can sanity-check why a candidate
  // ranks high or low ("the VOA row says 12,500m^2, none of the
  // candidates are within 50% of that, so the matcher's a bit blind").
  inputContext?: InputContext;

  // Action handlers
  onSave: (payload: SavePayload) => Promise<void>;
  // The unmount flush failed. Required, not optional: this panel is gone by the
  // time the rejection lands, so the parent is the only place a failed save can
  // still be told to the operator - and a caller that forgot to thread it would
  // drop the edit silently, which is the failure mode autosave exists to avoid.
  onFlushFailed: (message: string) => void;
  // Re-run this row's commercial lookups after its tenant domain was corrected.
  // The automatic pass only ever fills in MISSING answers, so without this a
  // corrected domain would sit next to the site count it was meant to fix.
  onRecountCommercial?: () => void;
  recountingCommercial?: boolean;
  // Persist ONE Ownership decision the moment the operator makes it, rather than
  // waiting for Save - the Contacts cells key off the SAVED company, so an unsaved
  // pick leaves them discovering contacts for the company it replaced. Required, with
  // `null` meaning "this surface doesn't save decisions per-pick" (/leads/search,
  // where the equivalent write is a VOA review that flips the row to Reviewed).
  //
  // Must report its OWN failures: the write can outlive this panel (the operator picks
  // an org and immediately clicks another row), so an error raised here would land on
  // a component that is already gone - the one failure that loses a pick would be the
  // one nobody sees.
  onSaveOrgDecision: ((patch: OrgDecisionPatch) => Promise<void>) | null;
  // Persist Has-existing-PV on its own, for the one mode where the normal Save
  // is unavailable: once the lead has a linked site the panel is read-only and
  // Save is disabled, because re-saving its selections cannot affect the site.
  // This flag is the exception - outreach is drafted per LEAD, so it still
  // changes the email after the site exists. Sibling of `onSaveOrgDecision` and
  // required for the same reason: `null` says "this surface has no per-pick
  // write" (/leads/search, where the row is not a lead yet and the flag rides
  // the VOA review instead). Like that one, the implementer must surface its own
  // failures - the panel rolls the switch back, but it may be unmounted by then.
  onSaveHasPv: ((hasPv: boolean) => Promise<void>) | null;
  // Promote the candidate to a Lead in one click (covers review +
  // promote in a single BE call). Stays on the row afterwards, so the
  // operator can see what they just made. Only wired on /leads/search;
  // /leads (where rows are already promoted Leads) doesn't need it.
  onPromote?: (payload: SavePayload) => Promise<void>;
  // Sibling of `onPromote` that asks for tags first via a modal. Wired
  // only on /leads/search alongside onPromote. Splits the no-tag default
  // path (high-volume churn) from the tag-attached path (operator wants
  // to label the lead).
  onPromoteWithTag?: (payload: SavePayload) => Promise<void>;
  // Deselect the current row and return to the overview map showing all
  // results. Wired only on /leads/search (the /leads inbox has no map);
  // the "< back to map" link only renders when this is provided.
  onBackToMap?: () => void;
  onReject?: () => Promise<void>;
  // True when the candidate has `rejectedAt` set. Drives the Reject/Revive
  // button label. Revive routes through the autosave path (the BE's review
  // endpoint already clears rejectedAt, which under the Viewed vocabulary is
  // exactly Rejected -> Viewed), so no separate callback is needed.
  isRejected?: boolean;
  // Where this row sits in the review cycle, rendered as a badge in the pinned
  // header so the operator can see it without scrolling to the buttons.
  // Required, and with an explicit "no-status" member, because a caller that
  // simply forgot it would silently show the wrong state: the leads inbox edits
  // promoted leads rather than search results and genuinely has none.
  reviewStatus: PanelReviewStatus;
  // True when the row is already a Lead (existingLeadId set). Hides the
  // Promote and Reject actions - promoting is a no-op the BE rejects as
  // `already_a_lead`, and neither is what the operator wants on a row that has
  // already been through the decision.
  isAlreadyLead?: boolean;
  // That Lead's id, so the panel can offer a way through to it instead.
  existingLeadId?: string | null;
  // Wired on /leads only: rightmost button "Remove from leads"
  // deletes the lead row (BE bulkDeleteLeads with the single id).
  // Not relevant on /leads/search where the row is a VOA candidate.
  onRemoveFromLeads?: () => Promise<void>;
  // Wired on /leads only: leftmost button "Create site" - reuses the
  // bulk-create-sites preview/confirm modal for the single lead.
  // Hidden on /leads/search (operator needs to promote to a Lead
  // first) and hidden on /leads when the lead already has a linked
  // platform site.
  onCreateSite?: () => Promise<void>;
  // When set, the Create site button renders disabled with this text
  // as the hover tooltip. Used when the lead can't currently be
  // turned into a site (e.g. no building polygon yet).
  createSiteDisabledReason?: string;
  // When the lead already has a linked site, this object drives a
  // read-only panel layout: hide the Location / Buildings / Tenant /
  // Landlord sections, render tenant + landlord names below the
  // map, swap Create-site for a "View site" link (new tab), rename
  // Skip -> Next, hide the Enrich button, and disable the Save
  // buttons. None of those surfaces are useful once a site exists -
  // the operator just navigates or removes the lead.
  linkedSite?: {
    leadId: string;
    siteId: string;
    siteUrl: string;
    tenantName: string | null;
    landlordName: string | null;
    // Total footprint (m²) of the linked site's building(s), shown below
    // the map in place of the lead's declared floor area.
    totalBuildingArea?: number;
    // The site's `totalInstalledCapacity` (kWp) - the system InRange would
    // install, NOT any existing array on the same roof. Labelled here exactly
    // as the outreach drafter labels it, so the figure an operator reads on the
    // lead is the figure the generated email quotes.
    installCapacityKwp?: number;
  } | null;
  onRefetch?: () => Promise<void>;
  onMarkAlreadyOnPlatform?: () => void;
  refetching?: boolean;
  // Operator-corrected address: persists the new address on the lead and
  // re-runs enrichment (geocode -> buildings -> HMLR -> CH) from it.
  // When omitted, the address header is read-only.
  onReenrichAddress?: (address: string) => Promise<void>;
  reenriching?: boolean;
  // Per-row "Enrich with paid APIs" runs the Google Places passes on
  // top of the cheap default data. Hidden once the row already has
  // premiumEnrichedAt.
  onPremiumEnrich?: () => Promise<void>;
  premiumEnriching?: boolean;
  // Agentic landlord research (design PR #1265). Kicks off the paid
  // research agent as an LRO; the parent polls it and refreshes the row.
  // `researching` covers both the kickoff mutation and the poll. When
  // omitted, the "Research landlord" button is not rendered.
  onResearchLandlord?: () => Promise<void>;
  researching?: boolean;
  // This lead is in a bulk-research batch but its turn hasn't started yet (the
  // batch runs one lead at a time). Distinct from `researching` so the button can
  // read "Queued" rather than "Researching..." until it goes active.
  researchQueued?: boolean;
  // Cancel this lead's queued research, removing it from the running batch while
  // the rest of the queue continues. Only offered while `researchQueued`.
  onCancelQueuedResearch?: () => void;
  // Live count of web searches the agent has run this pass (from the LRO
  // progress), shown on the button while researching. Null when unknown.
  researchSearchCount?: number | null;
  agenticResearchedAt?: string | null;
  // The AI-trace run id + subject ref from the most recent research run
  // this session, used by the "View AI trace" toggle. Null before a run.
  researchTraceRunId?: string | null;
  researchSubjectRef?: string | null;
  // The R24 decision block + R26 lists from the most recent research run, rendered
  // read-only in the AiResearchResult card. Null/absent for an older-shape run.
  agenticDecision?: AgenticDecision | null;
  // Step-5 people discovery (promoted-lead surface only). When set, the
  // self-contained "Find people (outreach)" card renders below the org
  // sections; the card owns its own kickoff/poll/selection. Null on the
  // VOA-search surface (no promoted lead id yet).
  peopleDiscovery?: {
    leadId: string;
    landlordDecision: Record<string, unknown> | null;
    tenantDecision: Record<string, unknown> | null;
    contactCandidates: ContactCandidates | null;
    contactEnrichments: ContactEnrichments | null;
    hubspotMatches: HubSpotMatches | null;
    // Where the automatic chain has got to for this lead - the SAME value the /leads
    // Status column renders, so the two panes cannot report different stages. Null on
    // the search surface, where there is no promoted lead for a chain to run on.
    pipelineStatus: LeadPipelineStatus | null;
    // Apollo credits already spent on this lead, keyed by role. Null on a surface
    // that has no figure to give.
    apolloCredits: Record<string, number> | null;
    // Drafted / emailed people for this lead, from the Status column's own poll, so
    // the per-person tags land with the column instead of after their own request.
    outreachContactIds: OutreachContactIdsByRole | null;
  } | null;
  // Per-pass enrichment timestamps so the "Matching against" block
  // can show the operator how stale each source is.
  enrichmentFetchedAt?: {
    geocodedAt: string | null;
    buildingResolvedAt: string | null;
    hmlrEnrichedAt: string | null;
    tenantEnrichedAt: string | null;
    epcEnrichedAt: string | null;
    premiumEnrichedAt: string | null;
  };
  // Sources for which the most recent enrichment attempt was
  // prevented (circuit breaker open / 5xx / 429 / transport error).
  // Empty list = clean run; the panel marks each affected row in the
  // Data fetched panel with a "(failed)" tag instead of just the
  // timestamp.
  enrichmentDegradedSources?: string[];
  // Bubbles up dirty state so the parent page can confirm() before
  // switching to a different row in the left table - the panel itself
  // only owns its own state, not the parent's "selectedSourceRef".
  onDirtyChange?: (dirty: boolean) => void;
  // Bubbles up the panel's *unsaved* selection so the parent's left-
  // panel table can reflect mid-edit changes immediately - e.g. the
  // operator clears the Landlord in the right panel and the left-
  // panel Landlord column blanks out before they hit Save. The
  // parent decides how to render: typically "show pending if dirty,
  // else show saved/default".
  //
  // `dirty` reflects whether the panel state differs from the
  // initials - parents should ignore pending state with dirty=false
  // (otherwise opening a row would mark it "unsaved" purely from
  // the defaulted-but-not-changed initial state).
  onPendingSelectionChange?: (pending: {
    buildingBkey: string | null;
    landlord: SelectedOrg | null;
    tenant: SelectedOrg | null;
    ownerOccupier: boolean;
    // Carried for the same reason as ownerOccupier: bulk Promote reads the row's
    // persisted value, so without the unsaved toggle here an operator who flips it
    // and then bulk-promotes silently loses the flag.
    hasPv: boolean;
    dirty: boolean;
  }) => void;

  // Which pane is rendering this panel, which drives the pane-specific
  // affordances. `"search"` = the `/leads/search` VOA-candidate triage pane;
  // `"lead"` = the `/leads` pane for an already-promoted lead about to become
  // a site. Required so both call sites make the choice explicitly.
  //   - Platform-org mapping (the "Platform org: X / New" summary line + the
  //     platform-org search box) shows only in `"lead"` - it's premature while
  //     triaging.
  //   - The "Then ..." runners-up peek shows only while triaging (`"search"`
  //     on a not-yet-promoted row); once it's a lead the decision is made, so
  //     the runners-up are hidden (still reachable via Change).
  mode: "search" | "lead";

  // Banners
  warnings?: React.ReactNode;
}

// The Ownership block's three pieces of state, moved as one: owner-occupier ties the
// landlord and tenant together, so they are only ever coherent when set together.
interface OrgSelection {
  landlord: SelectedOrg | null;
  tenant: SelectedOrg | null;
  ownerOccupier: boolean;
}

// What one Ownership action changes, and ONLY that: an omitted field means "leave the
// persisted value alone". Load-bearing, because the panel's displayed selection is not
// always something the operator chose - a saved landlord whose deed doesn't overlap the
// selected building is auto-cleared to null on mount (see the auto-validate effect), so
// writing the whole panel state on a TENANT pick would delete that landlord decision
// behind their back. The same reason the building pick is never in here: it keeps its
// explicit Save.
export interface OrgDecisionPatch {
  landlord?: SelectedOrg | null;
  tenant?: SelectedOrg | null;
  ownerOccupier?: boolean;
}

// Two picks are the same org only when every field that identifies one is the same.
// Name + source is not enough: a postcode routinely carries two HMLR deeds under one
// proprietor name, and the same company can appear twice in the Places results - the
// per-source id is what the picker itself uses to re-check the chosen row.
const sameOrg = (a: SelectedOrg | null, b: SelectedOrg | null): boolean =>
  a?.displayName === b?.displayName &&
  a?.source === b?.source &&
  (a?.orgId ?? null) === (b?.orgId ?? null) &&
  (a?.hmlrTitleNumber ?? null) === (b?.hmlrTitleNumber ?? null) &&
  (a?.chCompanyNumber ?? null) === (b?.chCompanyNumber ?? null) &&
  (a?.googlePlaceId ?? null) === (b?.googlePlaceId ?? null) &&
  (a?.osmId ?? null) === (b?.osmId ?? null);

export interface SavePayload {
  buildingBkey: string | null;
  landlord: SelectedOrg | null;
  tenant: SelectedOrg | null;
  ownerOccupier: boolean;
  // The operator's own existing-solar flag. Only ever what they set: a site-derived
  // existing PV is NOT folded in here, so saving a lead whose site declares solar
  // cannot silently write that into the lead's own flag and outlive the site's value.
  hasPv: boolean;
  // Set when the selected building was picked off-list via "Show all
  // available buildings" (an OSM polygon the resolver never offered). The
  // parent forwards it to PATCH /leads/<id>/decisions so the BE persists
  // it into polygon_candidates and the acceptedBkey resolves.
  newPolygonCandidate?: BuildingCandidate | null;
}

export interface InputContext {
  // Pretty-printed raw source data the operator can pop open in a
  // modal for full-fidelity debugging - "show me everything we know
  // about the row this came from". The shape varies by source: VOA
  // candidates pass the whole LeadCandidate JSON, CSV-imported leads
  // pass the original row + the persisted Lead row, etc.
  rawSource?: Record<string, unknown>;

  // Where the row came from ("VOA rating list 2023", "CSV: portfolio.xlsx").
  // Lets the operator know which schema the declared values originate from.
  sourceLabel: string;
  // The address as the source recorded it, before any geocoder /
  // building-polygon resolution. Surfaced in the "Matching against"
  // block so operators can sanity-check what the matchers were
  // actually pointed at.
  address: string | null;
  // What the source claimed about the building's footprint.
  declaredFloorAreaSqm: number | null;
  // What the source claimed about how many buildings make up the site.
  // VOA never declares this, so it's always null for VOA candidates;
  // CSV imports populate it from the `number_of_buildings` column on
  // the import row.
  declaredNumBuildings: number | null;
  // Geocoder + confidence label that produced the search anchor (i.e.
  // how trustworthy the lat/lon at the centre of the candidate map is).
  // "mapbox HIGH", "google MEDIUM", "postcodes_io POSTCODE_CENTROID".
  geocodeSummary: string | null;
  // Full per-source attempt list when available. Lets the operator see
  // which geocoders were asked, what confidence each returned, and how
  // far apart their answers were - so they can weigh the table's
  // distance metric against the anchor's reliability.
  geocodeAttempts: GeocodeAttempt[] | null;
  // Google Address Validation verdict for CSV-imported leads. Only
  // populated when the row went through the CSV-import path (AV is
  // not on the VOA path). Surfaces granularity + per-component
  // confirmation + inferred/replaced flags so the operator can spot
  // address-quality issues at a glance.
  addressVerdict?: AddressVerdict | null;
  // EPC non-domestic register hits at the row's postcode, most-recent
  // first. Used by the InputContextBlock to corroborate the declared
  // floor area + show the energy rating. Empty list means the EPC
  // pass ran but found no certs at the postcode (or the pass hasn't
  // run yet for older rows).
  epcMatches?: EpcMatch[];
}

interface OrgRow {
  key: string;
  displayName: string;
  source: SelectedOrg["source"];
  hmlrTitleNumber?: string | null;
  chCompanyNumber?: string | null;
  orgId?: string | null;
  // The platform Organisation name the BE fuzzy-matched this row to,
  // when one was found. Surfaced in its own column on the OrgSection
  // table so the operator can see whether a candidate already maps
  // to an existing org without having to parse the detail line.
  platformOrgName?: string | null;
  confidence: number | null;
  detail: string;
  // Lat/lon for Places + OSM rows (where we have a per-business
  // coordinate). Used by OrgSection's row hover to fly the map to
  // the business and render a transient marker. Null for CH +
  // HMLR + platform + manual.
  tenantLatLon?: [number, number] | null;
  asSelectedOrg: SelectedOrg;
  // Agentic-research provenance. On a native `source === "agentic"` row these
  // are the candidate's own. On a non-agentic winner they are carried over by
  // the dedupe when an agent result folded into a same-named row (see
  // `agenticCorroborated`), so the row can still render the agent's evidence.
  citations?: AgenticMatch["citations"];
  reasoning?: string;
  // True when the dedupe folded an agentic-research candidate into a
  // NON-agentic winner (an HMLR deed / tenant / platform row of the same
  // name): the row's owner has been independently corroborated by the agent.
  // Drives the "AI-corroborated" badge + surfaces the carried citations /
  // reasoning. Never set when the agentic row itself won (that row already
  // reads as source "agentic").
  agenticCorroborated?: boolean;
  // All synthetic keys that merged into this row during dedupe. The
  // pre-checked radio + the saved-selection-still-valid warning
  // both need to recognise a row as "selected" when *any* of its
  // underlying source-keys matched the saved selection's key, not
  // just the winning row's key. Without this a Costco saved as OSM
  // de-checks its own row when a Places "Costco Wholesale" with
  // higher confidence wins the dedupe.
  mergedKeys?: string[];
}

// Walk the raw-source JSON and replace any value at a key the operator
// is unlikely to want to read inline (polygon arrays, multi-polygon
// arrays, deed geometry) with `{}`. The "raw data" modal toggles
// between this stripped view (default) and the full geometry-bearing
// view. We don't try to detect polygon-shaped values structurally
// because a few non-polygon keys also carry coordinate-shaped data
// (e.g. `geocodeAttempts[].lat`); a key allow-list is more reliable.
const POLYGON_KEYS = new Set([
  "polygon",
  "polygons",
  "polygonData",
  "buildingPolygon",
  "buildingPolygons",
  "geometry",
  "geometries",
  "coordinates",
]);
const stripPolygonsForDisplay = (value: unknown, include: boolean): unknown => {
  if (include) return value;
  if (Array.isArray(value)) {
    return value.map((v) => stripPolygonsForDisplay(v, include));
  }
  if (value && typeof value === "object") {
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(value as Record<string, unknown>)) {
      if (POLYGON_KEYS.has(k)) {
        // Render the polygon as `{"excluded": true}` rather than `{}`
        // so the operator can see at a glance that the field exists
        // but was hidden by the toggle - vs. legitimately empty
        // upstream payload which would also serialise as `{}`.
        out[k] = { excluded: true };
      } else {
        out[k] = stripPolygonsForDisplay(v, include);
      }
    }
    return out;
  }
  return value;
};

const hmlrToOrgRow = (
  m: HmlrMatch & { orgId?: string | null; orgName?: string | null }
): OrgRow => {
  // Prefer the platform-org display name when it's set - the BE only
  // attaches it when a fuzzy match was found, so this is exactly the
  // case where the operator wants to see "B&Q" instead of "B & Q
  // PROPERTIES LIMITED". Original name goes in the detail line so
  // they can still see the legal HMLR name for traceability.
  const displayName = m.orgName ?? m.proprietorName;
  const detail = [
    m.orgName && m.orgName !== m.proprietorName
      ? `HMLR: "${m.proprietorName}"`
      : null,
    m.tenure ?? "tenure unknown",
    m.titleNumber ? m.titleNumber : null,
    m.distanceMetres != null ? `${Math.round(m.distanceMetres)}m` : null,
  ]
    .filter(Boolean)
    .join(" · ");
  return {
    key: `hmlr:${m.titleNumber ?? m.proprietorName}`,
    displayName,
    source: "hmlr",
    hmlrTitleNumber: m.titleNumber ?? null,
    confidence: m.confidence,
    detail,
    orgId: m.orgId ?? null,
    platformOrgName: m.orgName ?? null,
    asSelectedOrg: {
      orgId: m.orgId ?? null,
      displayName,
      source: "hmlr",
      hmlrTitleNumber: m.titleNumber ?? null,
    },
  };
};

// The top (confidence-desc) HMLR deed of an already-filtered list as a
// selectable owner, applying the auto-pick confidence floor. Null when the
// list is empty or its top deed is below the floor. Shared by the initial
// landlord default-pick (filtered to the default building) and the
// owner-occupier un-tick fallback (filtered to the SELECTED building), so
// both offer the same deed the visible options table would.
const topGatedHmlrLandlord = (filtered: HmlrMatch[]): SelectedOrg | null => {
  const top = filtered[0];
  if (!top) return null;
  // Single shared landlord rule (>= 50) rather than a caller-passed number, so
  // the panel default-pick, the results cell and the promote payload cannot
  // drift apart on what counts as auto-picked.
  if (!isLandlordAutoPickable(top.confidence)) return null;
  return hmlrToOrgRow(top).asSelectedOrg;
};

// Map an agentic landlord-research candidate to an OrgRow. Rendered in the
// same landlord table as HMLR deeds but as a distinct "agentic" source, with
// its citations + reasoning attached. The FE never DEFAULT-picks an agentic row
// (`defaultedInitialLandlord`, `totalConfidence`, and LeadSearchPage's
// `effectiveTopLandlord` read `hmlrMatches` only). It CAN, however, become the
// persisted `selectedLandlord` two ways: an explicit operator click, or the BE
// auto-picking the top research candidate when its confidence is over 60%
// (`operation_landlord_research._auto_pick_landlord_selection`) - both persist the
// identical `asSelectedOrg` shape below, so the panel renders them the same.
// The row's citations are viewed via the shared "View research" modal
// (AiResearchResult), which renders each citation URL as a live link only when
// http(s) (isSafeHttpUrl) - never a javascript:/data: scheme.

const agenticToOrgRow = (m: AgenticMatch): OrgRow => {
  const displayName = agenticDisplayName(m);
  const detail = [
    // A carried-over row is the SELECTED party preserved from an earlier run because
    // the latest one didn't return it. Say so first: its confidence and citations are
    // the older run's, and without the label it reads as a fresh finding.
    m.carriedForward ? "From an earlier run" : null,
    m.orgName && m.orgName !== m.proprietorName
      ? `Researched: "${m.proprietorName}"`
      : null,
    m.companyNumber ? `CH ${m.companyNumber}` : null,
    `${m.citations.length} source${m.citations.length === 1 ? "" : "s"}`,
  ]
    .filter(Boolean)
    .join(" · ");
  return {
    // Key on displayName (not companyNumber) so it round-trips through
    // asSelectedOrg, which carries only {orgId, displayName, source} - the
    // same shape platform/manual rows use.
    key: `agentic:${displayName}`,
    displayName,
    source: "agentic",
    confidence: m.confidence,
    detail,
    orgId: m.orgId ?? null,
    platformOrgName: m.orgName ?? null,
    citations: m.citations,
    reasoning: m.reasoning,
    asSelectedOrg: {
      orgId: m.orgId ?? null,
      displayName,
      source: "agentic",
    },
  };
};

const tenantToOrgRow = (
  m: TenantMatch & { orgId?: string | null; orgName?: string | null }
): OrgRow => {
  const isPlaces = m.source === "google_places";
  const isOsm = m.source === "osm";
  const isCh = !isPlaces && !isOsm;
  // Prefer the platform-org display name when set (same logic as
  // hmlrToOrgRow). The original source-side name is preserved in
  // the detail row so the operator can see what we stemmed from
  // and which legal entity the match targeted.
  const displayName = m.orgName ?? m.companyName;
  const platformDetail =
    m.orgName && m.orgName !== m.companyName
      ? isPlaces
        ? `Places: "${m.companyName}"`
        : isOsm
          ? `OSM: "${m.companyName}"`
          : `CH: "${m.companyName}"`
      : null;
  const detail = isPlaces
    ? [
        platformDetail,
        "from Google Places",
        m.distanceMetres != null ? `${Math.round(m.distanceMetres)}m` : null,
        // When the BE stemmed a city/branch suffix off the displayName
        // (e.g. "Costco Bristol" -> "Costco"), surface the original so
        // the operator can spot bad stems and report them.
        m.originalCompanyName && m.originalCompanyName !== m.companyName
          ? `stemmed from "${m.originalCompanyName}"`
          : null,
        m.registeredAddress,
      ]
        .filter(Boolean)
        .join(" · ")
    : isOsm
      ? [
          platformDetail,
          "from OpenStreetMap",
          m.distanceMetres != null ? `${Math.round(m.distanceMetres)}m` : null,
          // companyName is the OSM `operator` (the occupying company) whenever
          // it exists, so what's carried here is the facility name it beat.
          m.originalCompanyName && m.originalCompanyName !== m.companyName
            ? `facility: "${m.originalCompanyName}"`
            : null,
        ]
          .filter(Boolean)
          .join(" · ")
      : [
          platformDetail,
          m.companyNumber,
          m.companyStatus ?? "status unknown",
          m.sicCodes.length ? `SIC ${m.sicCodes.join(",")}` : null,
        ]
          .filter(Boolean)
          .join(" · ");
  // Distinct keys per source so a CH name + a Places name + an OSM
  // name that happen to match don't collide; OrgSection's name-based
  // dedupe still merges them downstream.
  const key = isPlaces
    ? `places:${m.googlePlaceId ?? m.companyName}`
    : isOsm
      ? `osm:${m.osmId ?? m.companyName}`
      : `ch:${m.companyNumber ?? m.companyName}`;
  // Per-business coordinate is set on Places + OSM matches; null
  // for CH (postcode-keyed). The FE row-hover handler uses it to
  // pan the map + render a transient marker.
  const hasLatLon =
    typeof m.latitude === "number" && typeof m.longitude === "number";
  const tenantLatLon: [number, number] | null = hasLatLon
    ? [m.latitude as number, m.longitude as number]
    : null;
  const source: SelectedOrg["source"] = isPlaces
    ? "google_places"
    : isOsm
      ? "osm"
      : "ch";
  const asSelectedOrg: SelectedOrg = isPlaces
    ? {
        orgId: m.orgId ?? null,
        displayName,
        source: "google_places",
        googlePlaceId: m.googlePlaceId ?? null,
      }
    : isOsm
      ? {
          orgId: m.orgId ?? null,
          displayName,
          source: "osm",
          osmId: m.osmId ?? null,
          // Carried onto the decision so people-discovery can hand Apollo
          // a precise domain instead of a fuzzy trading-name search.
          website: m.website ?? null,
        }
      : {
          orgId: m.orgId ?? null,
          displayName,
          source: "ch",
          chCompanyNumber: m.companyNumber ?? null,
        };
  return {
    key,
    displayName,
    source,
    chCompanyNumber: isCh ? (m.companyNumber ?? null) : null,
    confidence: m.confidence,
    detail,
    orgId: m.orgId ?? null,
    platformOrgName: m.orgName ?? null,
    tenantLatLon,
    asSelectedOrg,
  };
};

const sortRows = <T,>(
  rows: T[],
  cmp: (a: T, b: T) => number,
  dir: SortDirection
): T[] => {
  const sorted = [...rows].sort(cmp);
  return dir === "desc" ? sorted.reverse() : sorted;
};

// 2x2 right-panel layout: two equal columns; an expanded cell spans both.
const GRID_STYLE: React.CSSProperties = {
  display: "grid",
  gridTemplateColumns: "1fr 1fr",
  gap: 12,
  alignItems: "start",
};
const GROUP_LABEL_STYLE: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  letterSpacing: ".07em",
  textTransform: "uppercase",
  color: "#adb5bd",
  margin: "2px 2px 8px",
};

// Inline rather than an asset: theme-components' icon set has no sun (its solar
// icons are PV-panel glyphs) and this app carries no icon library, so a single
// 13px decoration is not worth either. Decorative only - the switch and its
// label already carry the state, so it is hidden from assistive tech.
const SunIcon = () => (
  <svg
    width="13"
    height="13"
    viewBox="0 0 24 24"
    fill="none"
    stroke="#e0a800"
    strokeWidth="2"
    strokeLinecap="round"
    aria-hidden="true"
    focusable="false"
    style={{ flex: "none" }}
  >
    <circle cx="12" cy="12" r="4.5" />
    <path d="M12 1.5v2.6M12 19.9v2.6M3.6 3.6l1.9 1.9M18.5 18.5l1.9 1.9M1.5 12h2.6M19.9 12h2.6M3.6 20.4l1.9-1.9M18.5 5.5l1.9-1.9" />
  </svg>
);

// Small solid chip for the landlord-research verdict (tile strip) and the
// per-row R26 adjudication status. Mirrors AiResearchResult's chip so the tile,
// the row, and the modal read as one surface (colour comes from the shared
// verdictDisplay / adjudicationDisplay helpers).
const RESEARCH_CHIP_STYLE: React.CSSProperties = {
  fontSize: 10,
  fontWeight: 500,
  padding: "1px 6px",
  borderRadius: 4,
  color: "#fff",
  flex: "none",
  whiteSpace: "nowrap",
};

// A soft (light-background, coloured-text) verdict tag for a candidate row - the
// mockup's `.tag`. Used for the short "Confirmed / Not the owner / Contradicted"
// (or confidence-band) label that precedes the one-line reason. 12% alpha keeps
// the text legible on the tint.
const researchTagStyle = (colour: string): React.CSSProperties => ({
  display: "inline-block",
  fontSize: 10.5,
  fontWeight: 600,
  padding: "1px 6px",
  borderRadius: 4,
  color: colour,
  background: `${colour}1f`,
  whiteSpace: "nowrap",
});

// A solid coloured confidence pill (the mockup's `.pct`) - white text on the
// red/amber/green `confColour`, tabular figures so a column of them lines up.
const confPillStyle = (pct: number | null): React.CSSProperties => ({
  display: "inline-block",
  fontSize: 11,
  fontWeight: 700,
  padding: "2px 7px",
  borderRadius: 5,
  color: "#fff",
  background: confColour(pct),
  fontVariantNumeric: "tabular-nums",
  whiteSpace: "nowrap",
});

// Per-lead dismissal of the "same domain -> likely owner-occupied" hint,
// persisted in localStorage so it stays dismissed across reloads and revisits
// (component state alone resets on the panel's per-row remount). Best-effort:
// if localStorage is unavailable (private mode / quota) the hint just won't
// persist its dismissal - it's a cosmetic nudge, not worth surfacing an error.
const OO_HINT_DISMISS_PREFIX = "inrange.leads.ooHintDismissed.";
const isOOHintDismissed = (leadId: string): boolean => {
  try {
    return window.localStorage.getItem(OO_HINT_DISMISS_PREFIX + leadId) === "1";
  } catch {
    return false;
  }
};
const rememberOOHintDismissed = (leadId: string): void => {
  try {
    window.localStorage.setItem(OO_HINT_DISMISS_PREFIX + leadId, "1");
  } catch {
    // localStorage unavailable - dismissal just won't persist.
  }
};

// `confColour` (red/amber/green for a 0-100 confidence) is imported from
// ./leadResearchDisplay so this panel and the AiResearchResult card can't drift.

// Same idea for the geocoder's labelled confidence (HIGH/MEDIUM/LOW).
const geocoderLabelColour = (label: string | null): string =>
  label === "HIGH" ? "#198754" : label === "MEDIUM" ? "#d9822b" : "#d9534f";

// Explanations shown on the headline confidence of each collapsed cell (and
// its expanded picker) so an operator can see what the number means without
// leaving the panel. Kept short; the per-row tooltips carry the detail.
const LOC_CONF_TIP =
  "Geocoder match confidence - how precisely the address was located.\n" +
  "HIGH: full street + number match. MEDIUM: street / postcode level. LOW: broader area only.";
const TENANT_CONF_TIP =
  "How likely this org occupies the building - from the match source, name similarity and distance to the building.";
const LANDLORD_CONF_TIP =
  "How likely this org owns the building - HMLR title-deed overlap + distance to the polygon, or AI-research corroboration.";

// Dedupe org rows by display name (case-insensitive), keeping the higher
// confidence and merging sources - the SAME logic OrgSection uses for its
// visible table, extracted so the collapsed summary and the expanded picker
// derive the selected row + runners-up from one place (surfaces must agree).
const dedupeOrgRows = (baseRows: OrgRow[]): OrgRow[] => {
  const map = new Map<string, OrgRow>();
  for (const row of baseRows) {
    const dedupeKey = row.displayName.toLowerCase();
    const prev = map.get(dedupeKey);
    if (!prev) {
      map.set(dedupeKey, { ...row, mergedKeys: [row.key] });
      continue;
    }
    const winner = (row.confidence ?? 0) > (prev.confidence ?? 0) ? row : prev;
    const agenticContributor =
      [prev, row].find(
        (r) => r.source === "agentic" || r.agenticCorroborated
      ) ?? null;
    const carryAgentic =
      winner.source !== "agentic" && agenticContributor != null;
    map.set(dedupeKey, {
      ...winner,
      detail: `${prev.detail} | ${row.detail}`,
      mergedKeys: [...(prev.mergedKeys ?? [prev.key]), row.key],
      ...(carryAgentic
        ? {
            agenticCorroborated: true,
            citations: winner.citations ?? agenticContributor.citations,
            reasoning: winner.reasoning ?? agenticContributor.reasoning,
          }
        : {}),
    });
  }
  return Array.from(map.values());
};

// Synthetic key for a saved SelectedOrg, mirroring the row-key shapes the
// *ToOrgRow helpers produce, so a saved pick re-checks its own row.
const orgSelectedKey = (selected: SelectedOrg | null): string | null =>
  !selected
    ? null
    : selected.source === "hmlr"
      ? `hmlr:${selected.hmlrTitleNumber ?? selected.displayName}`
      : selected.source === "ch"
        ? `ch:${selected.chCompanyNumber ?? selected.displayName}`
        : selected.source === "google_places"
          ? `places:${selected.googlePlaceId ?? selected.displayName}`
          : selected.source === "osm"
            ? `osm:${selected.osmId ?? selected.displayName}`
            : selected.source === "platform"
              ? `platform:${selected.orgId}`
              : selected.source === "agentic"
                ? `agentic:${selected.displayName}`
                : `manual:${selected.displayName}`;

// Find the deduped row a saved selection corresponds to (by synthetic key,
// a merged-away key, or a name fallback). Null when the selection is a
// platform/manual pick not present in the match list.
const findOrgRow = (
  rows: OrgRow[],
  selected: SelectedOrg | null
): OrgRow | null => {
  if (!selected) return null;
  const key = orgSelectedKey(selected);
  return (
    rows.find(
      (r) => r.key === key || (r.mergedKeys?.includes(key ?? "") ?? false)
    ) ??
    rows.find(
      (r) => r.displayName.toLowerCase() === selected.displayName.toLowerCase()
    ) ??
    null
  );
};

// Shared "Then ..." runners-up line under a collapsed cell's headline pick.
// Buildings render a colour dot + %, orgs render % + (ellipsised) name.
const PeekLine: React.FC<{
  items: (
    | { kind: "building"; conf: number | null; colour: string }
    | { kind: "org"; conf: number | null; name: string }
  )[];
  // Overrides the bare "Then" heading. An org cell that also shows a research decision
  // needs to say WHOSE runners-up these are: the decision line names the top pick, so a
  // "Then <other company>" directly under it reads as if the decision were about that
  // company (reported from the Severn Beach panel, where "Proceed with caution - write to
  // Lidl" sat under "Then 40% Norton Avonmouth").
  label?: string;
}> = ({ items, label }) => {
  if (items.length === 0) return null;
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        flexWrap: "wrap",
        gap: "4px 10px",
        marginTop: 6,
        paddingTop: 6,
        borderTop: "1px dotted #e9ecef",
        fontSize: 11.5,
        color: "#6c757d",
      }}
    >
      <span
        style={{
          flex: "0 0 100%",
          color: "#adb5bd",
          textTransform: "uppercase",
          fontSize: 10,
          letterSpacing: ".06em",
        }}
      >
        {label ?? "Then"}
      </span>
      {items.map((it, i) => (
        <span
          key={i}
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            maxWidth: "100%",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {it.kind === "building" && (
            <span
              style={{
                width: 9,
                height: 9,
                borderRadius: "50%",
                background: it.colour,
                border: "1px solid rgba(0,0,0,0.2)",
                flex: "none",
              }}
            />
          )}
          <span style={{ fontWeight: 700, color: confColour(it.conf) }}>
            {it.conf == null ? "-" : `${Math.round(it.conf)}%`}
          </span>
          {it.kind === "org" && (
            <WithTooltip text={it.name}>
              <span
                style={{
                  display: "inline-block",
                  maxWidth: 240,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  verticalAlign: "bottom",
                  fontWeight: 500,
                  cursor: "help",
                }}
              >
                {it.name}
              </span>
            </WithTooltip>
          )}
        </span>
      ))}
    </div>
  );
};

// Collapsed-cell summary for the Location cell: the address being used, then
// the picked geocoder's confidence (colour-graded) + source + a Google Maps
// link, and the geocoder-disagreement readout when the sources spread out.
const LocationSummary: React.FC<{
  address: string | null;
  attempts: GeocodeAttempt[];
  selected: [number, number] | null;
  mapCentre: [number, number] | null;
  // When the cell is expanded, the "View all on map" action folds onto the
  // geocoder-disagreement line (right-justified) rather than sitting alone at
  // the top of the expanded body.
  expanded: boolean;
  onViewAll: () => void;
  // The "geocoders disagree by X m" readout is triage signal for the search
  // pane; on the leads pane (a promoted row whose location is settled) it's
  // noise, so it's hidden there. "View all on map" (expanded only) stays.
  showGeocoderSpread: boolean;
  // Inline address edit, rendered right in the cell: the "edit" link swaps the
  // address line for this text box + Save & re-enrich (previously this jumped to
  // an editor at the very top of the panel, which was disorienting). The address
  // doubles as the site name on create. All omitted when re-enrichment isn't
  // wired.
  onEditName?: () => void;
  editing?: boolean;
  addressDraft?: string;
  onAddressDraftChange?: (value: string) => void;
  onSaveEdit?: () => void;
  onCancelEdit?: () => void;
  saving?: boolean;
}> = ({
  address,
  attempts,
  selected,
  mapCentre,
  expanded,
  onViewAll,
  showGeocoderSpread,
  onEditName,
  editing,
  addressDraft,
  onAddressDraftChange,
  onSaveEdit,
  onCancelEdit,
  saving,
}) => {
  const located = attempts.filter((a) => a.lat != null && a.lon != null);
  const sel =
    selected != null
      ? (located.find((a) => a.lat === selected[0] && a.lon === selected[1]) ??
        null)
      : null;
  const maxSpread = (() => {
    if (located.length < 2) return null;
    let max = 0;
    for (let i = 0; i < located.length; i++) {
      for (let j = i + 1; j < located.length; j++) {
        const d = haversineMetres(
          located[i].lat!,
          located[i].lon!,
          located[j].lat!,
          located[j].lon!
        );
        if (d > max) max = d;
      }
    }
    return max;
  })();
  // The Location cell is about the geocoded point, so its Maps link goes to
  // the picked geocode location (not the building centroid `mapCentre` picks
  // up once a building is selected); fall back to mapCentre when unlocated.
  const linkPoint = selected ?? mapCentre;
  return (
    <div>
      {editing && onCancelEdit ? (
        <div
          onClick={(e) => e.stopPropagation()}
          style={{
            display: "flex",
            alignItems: "center",
            gap: 6,
            flexWrap: "wrap",
          }}
        >
          <Form.Control
            size="sm"
            value={addressDraft ?? ""}
            onChange={(e) => onAddressDraftChange?.(e.target.value)}
            placeholder="Corrected full address"
            style={{ flex: "1 1 220px", minWidth: 200, fontSize: 14 }}
            autoFocus
            disabled={saving}
          />
          <Button
            size="sm"
            variant="primary"
            disabled={!(addressDraft ?? "").trim() || saving}
            onClick={(e) => {
              e.stopPropagation();
              onSaveEdit?.();
            }}
          >
            {saving ? "Re-enriching…" : "Save & re-enrich"}
          </Button>
          <Button
            size="sm"
            variant="link"
            disabled={saving}
            style={{ padding: 0, flex: "none" }}
            onClick={(e) => {
              e.stopPropagation();
              onCancelEdit();
            }}
          >
            Cancel
          </Button>
        </div>
      ) : (
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            gap: 6,
            fontWeight: 600,
            fontSize: 14,
          }}
        >
          <span>
            {address ? (
              formatAddressCasing(address)
            ) : (
              <span style={{ color: "#adb5bd" }}>No address</span>
            )}
          </span>
          {onEditName && (
            <Button
              size="sm"
              variant="link"
              style={{
                fontSize: 12,
                fontWeight: 400,
                padding: 0,
                flex: "none",
              }}
              onClick={(e) => {
                e.stopPropagation();
                onEditName();
              }}
            >
              edit
            </Button>
          )}
        </div>
      )}
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          marginTop: 3,
          fontSize: 12.5,
          color: "#6c757d",
        }}
      >
        {sel?.confidenceLabel && (
          <WithTooltip text={LOC_CONF_TIP}>
            <span
              style={{
                fontWeight: 700,
                color: geocoderLabelColour(sel.confidenceLabel),
                cursor: "help",
              }}
            >
              {sel.confidenceLabel}
            </span>
          </WithTooltip>
        )}
        <span
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            whiteSpace: "nowrap",
          }}
        >
          {sel?.source && (
            <Badge bg="secondary" style={{ fontWeight: 400 }}>
              {sel.source}
            </Badge>
          )}
          {linkPoint && (
            <a
              href={googleMapsLink(linkPoint[0], linkPoint[1])}
              target="_blank"
              rel="noopener noreferrer"
              onClick={(e) => e.stopPropagation()}
              style={{ fontSize: 11.5, whiteSpace: "nowrap" }}
            >
              View in Google Maps
            </a>
          )}
        </span>
      </div>
      {((showGeocoderSpread && maxSpread != null) || expanded) && (
        <div
          style={{
            marginTop: 4,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 8,
          }}
        >
          <span
            style={{
              fontSize: 11,
              fontWeight: 600,
              lineHeight: 1,
              color:
                !showGeocoderSpread || maxSpread == null
                  ? "transparent"
                  : maxSpread < 25
                    ? "#198754"
                    : maxSpread < 100
                      ? "#d9822b"
                      : "#d9534f",
            }}
          >
            {showGeocoderSpread && maxSpread != null
              ? `geocoders disagree by ${Math.round(maxSpread)} m`
              : ""}
          </span>
          {expanded && (
            <Button
              size="sm"
              variant="link"
              style={{ fontSize: 11, padding: 0, flex: "none" }}
              onClick={(e) => {
                e.stopPropagation();
                onViewAll();
              }}
            >
              View all on map
            </Button>
          )}
        </div>
      )}
    </div>
  );
};

// Collapsed-cell summary for the Buildings cell: the selected polygon's
// colour + confidence + area + distance, then a peek at the runners-up.
const BuildingsSummary: React.FC<{
  building: BuildingCandidate | null;
  colour: string | null;
  peek: { conf: number | null; colour: string }[];
  // Show the "Then ..." runners-up peek (search-pane triage only).
  showRunnersUp: boolean;
}> = ({ building, colour, peek, showRunnersUp }) => {
  if (!building)
    return (
      <span style={{ color: "#6c757d", fontSize: 13 }}>
        No building selected
      </span>
    );
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
        }}
      >
        {colour && (
          <span
            style={{
              width: 11,
              height: 11,
              borderRadius: "50%",
              background: colour,
              border: "1px solid rgba(0,0,0,0.2)",
              flex: "none",
            }}
          />
        )}
        <WithTooltip text={formatBuildingMatchTooltip(building)}>
          <span
            style={{
              fontWeight: 700,
              fontSize: 14,
              lineHeight: 1,
              color: confColour(building.confidence),
              cursor: "help",
            }}
          >
            {building.confidence == null
              ? "-"
              : `${Math.round(building.confidence)}%`}
          </span>
        </WithTooltip>
        <span
          style={{
            fontSize: 12,
            lineHeight: 1,
            color: "#6c757d",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {building.surfaceAreaSqm?.toFixed(0) ?? "-"} m&sup2;
        </span>
        <span
          style={{
            fontSize: 12,
            lineHeight: 1,
            color: "#6c757d",
            fontVariantNumeric: "tabular-nums",
          }}
        >
          {Math.round(building.distanceMetres)} m away
        </span>
      </div>
      {showRunnersUp && (
        <PeekLine
          items={peek.map((p) => ({
            kind: "building" as const,
            conf: p.conf,
            colour: p.colour,
          }))}
        />
      )}
    </div>
  );
};

// Collapsed-cell summary for a Tenant / Landlord / Owner-occupier cell: the
// picked org's name, confidence (colour-graded) + source, the platform org it
// maps to (or "New"), a Clear-selection link, and a peek at the runners-up.
const OrgSummary: React.FC<{
  selected: SelectedOrg | null;
  rows: OrgRow[];
  confTip: string;
  onClear: () => void;
  // Show the "Platform org: X / New" mapping line (leads pane only).
  showPlatformOrg: boolean;
  // Show the "Then ..." runners-up peek (search-pane triage only).
  showRunnersUp: boolean;
  // The landlord-research decision strip (verdict chip + decision line + "View
  // research"), rendered between the selected pick and the runners-up peek. It belongs
  // to the SELECTED candidate, so it has to sit above the runners-up rather than after
  // them - otherwise the decision reads as being about the runner-up it follows.
  decisionStrip?: React.ReactNode;
}> = ({
  selected,
  rows,
  confTip,
  onClear,
  showPlatformOrg,
  showRunnersUp,
  decisionStrip,
}) => {
  if (!selected)
    return (
      <div>
        <div style={{ color: "#6c757d", fontSize: 13 }}>
          {noSelectionOptionsText(rows.length)}
        </div>
        {/* No pick yet is exactly when the research verdict matters most, so it renders
            here too - this early return sits in front of the shared strip below. */}
        {decisionStrip}
      </div>
    );
  const row = findOrgRow(rows, selected);
  const conf = row?.confidence ?? null;
  const platformName =
    row?.platformOrgName ??
    (selected.source === "platform" ? selected.displayName : null);
  const others = [...rows]
    .filter((r) => r !== row)
    .sort((a, b) => (b.confidence ?? 0) - (a.confidence ?? 0))
    .slice(0, 4);
  return (
    <div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          minWidth: 0,
        }}
      >
        <span
          style={{
            fontWeight: 600,
            fontSize: 14,
            // Needs room for descenders (the "g" in "Digital") - `overflow:
            // hidden` for the ellipsis clips them vertically at lineHeight 1.
            lineHeight: 1.35,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {selected.displayName}
        </span>
        <button
          type="button"
          onClick={(e) => {
            e.stopPropagation();
            onClear();
          }}
          style={{
            fontSize: 11,
            lineHeight: 1,
            padding: 0,
            border: "none",
            background: "none",
            color: "#0d6efd",
            cursor: "pointer",
            flex: "none",
          }}
        >
          Clear
        </button>
      </div>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          gap: 8,
          flexWrap: "wrap",
          marginTop: 3,
          fontSize: 12.5,
          color: "#6c757d",
        }}
      >
        {conf != null && (
          <WithTooltip text={confTip}>
            <span
              style={{
                fontWeight: 700,
                lineHeight: 1,
                color: confColour(conf),
                cursor: "help",
              }}
            >
              {Math.round(conf)}%
            </span>
          </WithTooltip>
        )}
        <Badge bg="secondary" style={{ fontWeight: 400 }}>
          {selected.source}
        </Badge>
      </div>
      {showPlatformOrg && (
        <div style={{ marginTop: 4, fontSize: 12, color: "#6c757d" }}>
          Platform org:{" "}
          {platformName ? (
            <span style={{ color: "#212529", fontWeight: 500 }}>
              {platformName}
            </span>
          ) : (
            <span
              style={{
                display: "inline-block",
                border: "1px dashed #ced4da",
                borderRadius: 4,
                padding: "0 6px",
                fontSize: 11,
                fontWeight: 600,
                color: "#6c757d",
              }}
            >
              New
            </span>
          )}
        </div>
      )}
      {decisionStrip}
      {showRunnersUp && (
        <PeekLine
          label={
            decisionStrip
              ? "Other candidates considered, ranked lower"
              : undefined
          }
          items={others.map((o) => ({
            kind: "org" as const,
            conf: o.confidence,
            name: o.displayName,
          }))}
        />
      )}
    </div>
  );
};

export const LeadEditPanel: React.FC<LeadEditPanelProps> = ({
  address,
  buildingCandidates,
  hmlrMatches,
  tenantMatches,
  agenticMatches,
  tenantCommercial,
  leadValue,
  initialBuildingBkey,
  initialLandlord,
  initialTenant,
  initialOwnerOccupier,
  initialHasPv,
  siteExistingPvKwp,
  fallbackLat,
  fallbackLon,
  leadTags,
  proximityContext,
  duplicateOfAddress,
  inputContext,
  onSave,
  onFlushFailed,
  onRecountCommercial,
  recountingCommercial,
  onSaveOrgDecision,
  onSaveHasPv,
  onPromote,
  onPromoteWithTag,
  onBackToMap,
  onReject,
  isRejected,
  reviewStatus,
  isAlreadyLead,
  existingLeadId,
  onRemoveFromLeads,
  onCreateSite,
  createSiteDisabledReason,
  linkedSite,
  onRefetch,
  refetching,
  onReenrichAddress,
  reenriching,
  onPremiumEnrich,
  premiumEnriching,
  onResearchLandlord,
  researching,
  researchQueued,
  onCancelQueuedResearch,
  researchSearchCount,
  agenticResearchedAt,
  researchTraceRunId,
  agenticDecision,
  researchSubjectRef,
  peopleDiscovery,
  enrichmentFetchedAt,
  enrichmentDegradedSources,
  onDirtyChange,
  onPendingSelectionChange,
  mode,
  warnings,
}) => {
  // Pane-derived affordances (see the `mode` prop doc). Platform-org mapping
  // is a leads-pane concern; the runners-up peek is a search-pane concern
  // (hidden once the row is a lead - on the leads pane, or an already-promoted
  // row still shown on the search pane).
  const showPlatformOrg = mode === "lead";
  const showRunnersUp = mode === "search" && !isAlreadyLead;
  // Opt-in landlord-research AI-trace panel: only fetched when the
  // operator expands it (traces can be large). Keyed on the run just
  // completed this session.
  const [showResearchTrace, setShowResearchTrace] = useState(false);
  const researchTrace = useLandlordResearchTrace(
    researchSubjectRef ?? null,
    researchTraceRunId ?? null,
    showResearchTrace
  );
  // "View research" modal: the full landlord-research output (decision block +
  // candidates/evidence + adjudications) shown on demand rather than pinned to
  // the top of the panel. Opened from the Landlord tile's recommendation strip
  // and from each candidate row's "Evidence" link; the latter passes the
  // clicked candidate name so the modal scrolls to + highlights it.
  const [showResearchModal, setShowResearchModal] = useState(false);
  const [researchModalHighlight, setResearchModalHighlight] = useState<
    string | null
  >(null);
  const openResearchModal = useCallback((candidateName?: string | null) => {
    setResearchModalHighlight(candidateName ?? null);
    setShowResearchModal(true);
  }, []);
  // "Related parties (N)" disclosure below the Landlord picker, collapsed by
  // default. One landlord per panel, so one open/close flag suffices.
  const [relatedPartiesOpen, setRelatedPartiesOpen] = useState(false);

  // When no explicit selection has been saved yet, pre-select the top-
  // ranked candidate so the operator sees the matcher's pick reflected
  // on the map and in the table. They can still change it before
  // saving; `dirty` tracks against the original `initialBuildingBkey`
  // (null) so a non-edit save is a no-op.
  const defaultedInitialBuildingBkey =
    initialBuildingBkey ?? buildingCandidates[0]?.bkey ?? null;
  // Auto-pick the landlord and tenant the same way the left-table
  // columns do: filtered HMLR row that overlaps the default building
  // for landlord, top-confidence tenant for tenant. The operator can
  // immediately Save these as the review without changing anything,
  // which is the common case when the matcher's pick is correct.
  // Only auto-pick when the row hasn't been reviewed yet (no saved
  // selection); a previously-saved selection always wins.

  const defaultedInitialLandlord = useMemo<SelectedOrg | null>(() => {
    if (initialLandlord) return initialLandlord;
    const defaultBuilding = buildingCandidates[0];
    const buildingBbox = defaultBuilding
      ? polygonBbox(defaultBuilding.polygon)
      : null;
    // IMPORTANT: this filter must match `filteredHmlrMatches` below.
    // If the default-pick uses a looser test than the visible table,
    // the FE pre-selects an HMLR row the table doesn't show, which
    // fires the "not in current matches" warning on a brand-new
    // row. The two filters are kept in sync via polygonsOverlap.
    const filtered =
      !buildingBbox || !defaultBuilding
        ? hmlrMatches
        : hmlrMatches.filter((m) => {
            if (!m.polygon) return true;
            const mb = m.bbox ?? polygonBbox(m.polygon);
            if (mb && !bboxesOverlap(buildingBbox, mb)) return false;
            return polygonsOverlap(defaultBuilding.polygon, m.polygon, {
              lat: defaultBuilding.centroidLat,
              lon: defaultBuilding.centroidLon,
            });
          });
    return topGatedHmlrLandlord(filtered);
  }, [initialLandlord, hmlrMatches, buildingCandidates]);
  const defaultedInitialTenant = useMemo<SelectedOrg | null>(() => {
    if (initialTenant) return initialTenant;
    // tenantMatches is CH-then-OSM-then-Places concatenation order on
    // the BE - within each source the rows are sorted by confidence,
    // but across sources the first source's top wins regardless of
    // its confidence. We pick by actual confidence instead so the
    // pre-selected tenant matches the visually-top row.
    const top = [...tenantMatches].sort(
      (a, b) => (b.confidence ?? 0) - (a.confidence ?? 0)
    )[0];
    if (!top) return null;
    if (!isAutoPickable(top.confidence)) return null;
    return tenantToOrgRow(top).asSelectedOrg;
  }, [initialTenant, tenantMatches]);

  const [buildingBkey, setBuildingBkey] = useState<string | null>(
    defaultedInitialBuildingBkey
  );
  // "Show all available buildings" state: the toggle, plus the OSM
  // buildings the operator has clicked (kept locally + merged into the
  // candidate pool so they render in the table/map and can be saved).
  const [showAllBuildings, setShowAllBuildings] = useState(false);
  const [extraCandidates, setExtraCandidates] = useState<BuildingCandidate[]>(
    []
  );
  // An off-list building the operator picked is held here until a save moves it
  // into `buildingCandidates` (the BE persists `newPolygonCandidate` into the
  // resolver list). From then on the resolver copy is the one that renders - the
  // panel used to rely on a remount to drop the local copy, and autosave no longer
  // remounts it.
  const allCandidates = useMemo(
    () => [
      ...buildingCandidates,
      ...extraCandidates.filter(
        (c) => !buildingCandidates.some((b) => b.bkey === c.bkey)
      ),
    ],
    [buildingCandidates, extraCandidates]
  );
  // The selection to fall back to when an exploratory OSM pick is
  // de-selected. Captured the first time the operator dives from a
  // resolver-offered candidate into an off-list one, so undoing the OSM
  // click restores the prior building rather than nulling the selection
  // (a null buildingBkey Saves as polygonDecision: null, which would
  // wipe a previously-saved building). Held across extra->extra hops.
  const preExtraSelectionRef = useRef<string | null>(null);
  // Single chokepoint for changing the building selection. An off-list
  // building (only in the list because the operator clicked it via "Show
  // all available buildings") exists ONLY while it's the selection, so
  // it's dropped the moment the selection moves off it - whether that's
  // a de-select, a resolver-candidate click, or a different off-list
  // click. That keeps at most one off-list row in the table and never
  // leaves an orphaned one behind.
  const selectBuilding = useCallback(
    (next: string | null) => {
      if (buildingBkey !== null && buildingBkey !== next) {
        setExtraCandidates((prev) =>
          prev.filter((c) => c.bkey !== buildingBkey)
        );
      }
      setBuildingBkey(next);
    },
    [buildingBkey]
  );
  const handlePickOsmBuilding = useCallback(
    (cand: BuildingCandidate) => {
      if (!extraCandidates.some((c) => c.bkey === buildingBkey)) {
        preExtraSelectionRef.current = buildingBkey;
      }
      setExtraCandidates((prev) =>
        prev.some((c) => c.bkey === cand.bkey) ? prev : [...prev, cand]
      );
      // selectBuilding drops the previously-selected off-list building
      // (if any) as the selection moves to `cand`.
      selectBuilding(cand.bkey);
    },
    [buildingBkey, extraCandidates, selectBuilding]
  );
  // Row/polygon click. Selecting works for any candidate; de-selecting
  // (clicking the already-selected one) additionally removes it from the
  // list when it's an off-list building, falling back to whatever was
  // selected before the operator started exploring OSM buildings.
  // Resolver-offered candidates keep plain radio semantics (re-clicking
  // the selected one is a no-op).
  const handleSelectBuilding = useCallback(
    (bkey: string) => {
      const isExtra = extraCandidates.some((c) => c.bkey === bkey);
      if (buildingBkey === bkey && isExtra) {
        selectBuilding(preExtraSelectionRef.current);
      } else {
        selectBuilding(bkey);
      }
    },
    [buildingBkey, extraCandidates, selectBuilding]
  );
  const [landlord, setLandlord] = useState<SelectedOrg | null>(
    defaultedInitialLandlord
  );
  const [tenant, setTenant] = useState<SelectedOrg | null>(
    defaultedInitialTenant
  );
  const [ownerOccupier, setOwnerOccupier] =
    useState<boolean>(initialOwnerOccupier);
  const [hasPv, setHasPv] = useState<boolean>(initialHasPv);
  // Deliberately asymmetric. A site that declares existing solar is a positive
  // record, so the toggle renders on and disabled with the source named - correct
  // it on the site's Demand tab rather than argue with it here. A site that
  // declares none is silence, not evidence of absence, so the operator can still
  // flag the roof. That asymmetry is exactly the BE's
  // `lead.hasPv OR site_kwp > 0`.
  const derivedHasPv = (siteExistingPvKwp ?? 0) > 0;
  const effectiveHasPv = hasPv || derivedHasPv;
  // Persisting the flag once a site exists needs its own request: the panel is
  // read-only in that mode and Save is disabled, on the grounds that re-saving
  // the lead's selections cannot affect the site. This flag is the exception -
  // outreach is drafted per LEAD, so it still changes the email after the site
  // is created.
  const [hasPvSaving, setHasPvSaving] = useState(false);
  const [hasPvSaveError, setHasPvSaveError] = useState<string | null>(null);
  const writesHasPvImmediately = Boolean(linkedSite && onSaveHasPv);
  // Dismissal of the "landlord + tenant resolved to the same domain -> likely
  // owner-occupied" hint (see the Ownership section), persisted per lead in
  // localStorage so it stays dismissed across reloads and revisiting the row.
  // Lazy-init reads the persisted value on mount (no flash on a remount); the
  // effect below re-syncs when the selected lead changes without a remount.
  const [dismissedSameDomainOOHint, setDismissedSameDomainOOHint] =
    useState<boolean>(() =>
      peopleDiscovery?.leadId != null
        ? isOOHintDismissed(peopleDiscovery.leadId)
        : false
    );
  useEffect(() => {
    setDismissedSameDomainOOHint(
      peopleDiscovery?.leadId != null
        ? isOOHintDismissed(peopleDiscovery.leadId)
        : false
    );
  }, [peopleDiscovery?.leadId]);
  // Which of the four right-panel cells is expanded (showing its full picker).
  // Null = all collapsed to their one-line summary. One at a time: opening a
  // cell collapses whichever was open.
  const [expandedCell, setExpandedCell] = useState<
    | "location"
    | "buildings"
    | "tenant"
    | "landlord"
    | "contacts-landlord"
    | "contacts-tenant"
    | "contacts-landlord-details"
    | "contacts-tenant-details"
    | null
  >(null);
  const toggleCell = useCallback(
    (
      cell:
        | "location"
        | "buildings"
        | "tenant"
        | "landlord"
        | "contacts-landlord"
        | "contacts-tenant"
        | "contacts-landlord-details"
        | "contacts-tenant-details"
    ) => setExpandedCell((c) => (c === cell ? null : cell)),
    []
  );
  const [saving, setSaving] = useState(false);
  // Manual-address edit state. `editingAddress` toggles the header
  // between the read-only address and an input + "Save & re-enrich".
  const [editingAddress, setEditingAddress] = useState(false);
  const [addressDraft, setAddressDraft] = useState(address ?? "");
  // The linked-site mode has no save of its own (Save was disabled there), so
  // autosave stays off rather than writing selections the surface treats as
  // read-only.
  const autosaveDisabled = linkedSite != null;
  const [error, setError] = useState<string | null>(null);
  // One-shot map viewport request - imperatively triggered by the
  // operator clicking "View all" in a section header or selecting a
  // row in the Location table. The map controller applies it then
  // resets to null so manual panning isn't fought.
  const [mapViewportRequest, setMapViewportRequest] =
    useState<MapViewportRequest | null>(null);

  // Hovered HMLR-deed title number. Set on row mouseenter in the
  // Landlord / Owner-occupier OrgSections, cleared on mouseleave.
  // Drives a transient purple polygon overlay on the map so the
  // operator can see which deed boundary corresponds to each row in
  // the table without committing to a selection.
  const [hoveredHmlrTitleNumber, setHoveredHmlrTitleNumber] = useState<
    string | null
  >(null);

  // Hovered Places/OSM tenant. Drives a transient pink marker on
  // the map at the business's lat/lon, plus auto-pans the map to
  // make sure the marker is visible. Cleared on mouseleave.
  const [hoveredTenantLatLon, setHoveredTenantLatLon] = useState<
    [number, number] | null
  >(null);

  // Auto-pan when a tenant row is hovered. Same fly-to viewport
  // request the rest of the panel uses; doesn't change zoom (just
  // ensures the marker is on-screen even if it sits at the edge of
  // the search radius).
  useEffect(() => {
    if (!hoveredTenantLatLon) return;
    setMapViewportRequest({
      kind: "fly-to",
      lat: hoveredTenantLatLon[0],
      lon: hoveredTenantLatLon[1],
    });
  }, [hoveredTenantLatLon]);

  // Hovered building bkey (from the BuildingTable below the map).
  // Used to bump the polygon's outline weight + colour while the
  // cursor is over its row, so the operator can quickly correlate
  // table rows with map polygons without clicking through.
  const [hoveredBuildingBkey, setHoveredBuildingBkey] = useState<string | null>(
    null
  );

  // Hovered geocoder-attempt lat/lon (from the LocationSection table
  // above the map). Bumps the matching CircleMarker's radius +
  // weight while hovered so the operator can correlate table rows
  // with pins on the map.
  const [hoveredLocationLatLon, setHoveredLocationLatLon] = useState<
    [number, number] | null
  >(null);

  // Operator-selected location for the "what should the matcher have
  // anchored on?" debug. Defaults to the geocoder that was actually
  // picked at search time. Switching this re-derives building distance
  // / confidence on the FE; the backend has already pre-fetched
  // buildings against every geocoder attempt so no round trip is
  // needed when the operator clicks a different attempt.
  const pickedAttempt =
    inputContext?.geocodeAttempts?.find((a) => a.wasPicked) ?? null;
  const [selectedLocation, setSelectedLocation] = useState<
    [number, number] | null
  >(
    pickedAttempt && pickedAttempt.lat != null && pickedAttempt.lon != null
      ? [pickedAttempt.lat, pickedAttempt.lon]
      : fallbackLat != null && fallbackLon != null
        ? [fallbackLat, fallbackLon]
        : null
  );

  // Operator-initiated location pick (table row click in LocationSection
  // or pin click on the map). Always recentres the map on the new pin
  // and resets zoom to INITIAL_MAP_ZOOM - building-fit clicks zoom in,
  // and the operator expects switching anchor to reset that view.
  const selectLocation = useCallback((latLon: [number, number]) => {
    setSelectedLocation(latLon);
    setMapViewportRequest({
      kind: "fly-to",
      lat: latLon[0],
      lon: latLon[1],
      zoom: INITIAL_MAP_ZOOM,
    });
  }, []);

  // When the subject changes (different row clicked), reset edit state.
  const subjectKey = `${address ?? ""}:${buildingCandidates.length}`;
  const lastSubjectKey = useRef(subjectKey);
  useEffect(() => {
    if (lastSubjectKey.current !== subjectKey) {
      lastSubjectKey.current = subjectKey;
      setBuildingBkey(defaultedInitialBuildingBkey);
      setLandlord(defaultedInitialLandlord);
      setTenant(defaultedInitialTenant);
      setOwnerOccupier(initialOwnerOccupier);
      setHasPv(initialHasPv);
      // Drop any owner-occupier landlord snapshot from the previous lead so an
      // un-tick on this lead can't restore the wrong row's landlord.
      preOwnerOccupierLandlordRef.current = null;
      // Likewise what this panel has already written: it describes the previous lead.
      lastWrittenOrgsRef.current = null;
      setError(null);
      setSelectedLocation(
        pickedAttempt && pickedAttempt.lat != null && pickedAttempt.lon != null
          ? [pickedAttempt.lat, pickedAttempt.lon]
          : fallbackLat != null && fallbackLon != null
            ? [fallbackLat, fallbackLon]
            : null
      );
      // Map auto-fit happens in a separate `useEffect` keyed on
      // `buildingBkey + buildingCandidates` below - that way it covers
      // both row-switch (state reset above) and explicit building
      // clicks within the same row, with one consistent code path.
    }
  }, [
    subjectKey,
    defaultedInitialBuildingBkey,
    defaultedInitialLandlord,
    defaultedInitialTenant,
    initialOwnerOccupier,
    initialHasPv,
    pickedAttempt,
    fallbackLat,
    fallbackLon,
    buildingCandidates,
  ]);

  // Auto-fit the map to whatever building is currently selected. Fires
  // on subject change (row-switch reset above swaps the buildingBkey
  // to the new row's default) AND on explicit building clicks (which
  // also call setBuildingBkey). One consistent code path so we can't
  // forget to fit on one of the entry points.
  useEffect(() => {
    const candidate = buildingCandidates.find((b) => b.bkey === buildingBkey);
    if (candidate) {
      setMapViewportRequest({
        kind: "fit-polygon",
        polygon: candidate.polygon,
      });
    } else if (buildingCandidates.length > 0) {
      setMapViewportRequest({ kind: "all-buildings" });
    }
  }, [buildingBkey, buildingCandidates]);

  // The `dirty` calculation + emit/blocker effects all sit below
  // `filteredHmlrMatches` so the auto-clear-aware dirty calc can
  // reference it. See the dirty calc comment for why we exclude
  // auto-clears from "operator made an edit".

  // Re-score each persisted building candidate against the operator's
  // currently selected location. Buildings further than the picked
  // anchor will get a different distance + confidence than the backend
  // computed; we let the FE override so switching location instantly
  // reorders the candidate table without a round-trip.
  const scoredBuildings = useMemo(() => {
    if (!selectedLocation) return allCandidates;
    const [lat, lon] = selectedLocation;
    return allCandidates.map((b) => {
      const distance = distanceToPolygonMetres(b.polygon, lat, lon);
      const confidence = matchConfidence(
        distance,
        b.surfaceAreaSqm,
        inputContext?.declaredFloorAreaSqm ?? null
      );
      return { ...b, distanceMetres: distance, confidence };
    });
  }, [allCandidates, selectedLocation, inputContext]);

  const selectedBuilding = useMemo(
    () => scoredBuildings.find((b) => b.bkey === buildingBkey) ?? null,
    [scoredBuildings, buildingBkey]
  );
  const mapCentre: [number, number] | null = selectedBuilding
    ? [selectedBuilding.centroidLat, selectedBuilding.centroidLon]
    : selectedLocation;

  // Restrict the Landlord (HMLR) list to deeds whose polygon contains
  // the currently selected building's centroid. The backend fetches
  // deeds across every candidate building's bbox, so switching
  // buildings on the right panel doesn't require a re-fetch - just a
  // re-filter here.
  //
  // Was previously bbox-vs-bbox overlap, which is too loose: a deed's
  // bbox can engulf a building the deed doesn't actually cover (long
  // strip plots, L-shaped industrial-estate deeds, etc.). The
  // operator hit this on BS10/30795351000 - a deed in the options
  // list whose polygon clearly didn't cover the building. Switching
  // to centroid-in-polygon catches that case while staying cheap (no
  // turf.js, no full polygon-intersection).
  const selectedBuildingBbox = useMemo(
    () => (selectedBuilding ? polygonBbox(selectedBuilding.polygon) : null),
    [selectedBuilding]
  );
  const filteredHmlrMatches = useMemo(() => {
    if (!selectedBuilding || !selectedBuildingBbox) return hmlrMatches;
    return hmlrMatches.filter((m) => {
      // Historical records without a polygon: keep visible (we have
      // no way to test overlap, and the operator can still pick).
      if (!m.polygon) return true;
      // Cheap pre-reject: if the bboxes don't overlap, the polygons
      // can't either.
      const mb = m.bbox ?? polygonBbox(m.polygon);
      if (mb && !bboxesOverlap(selectedBuildingBbox, mb)) return false;
      // Geometric test: real polygon-overlap heuristic (centroid +
      // vertex-in-polygon, both directions). Catches L-shaped deeds
      // where the strict centroid test would miss legitimate matches.
      return polygonsOverlap(selectedBuilding.polygon, m.polygon, {
        lat: selectedBuilding.centroidLat,
        lon: selectedBuilding.centroidLon,
      });
    });
  }, [hmlrMatches, selectedBuilding, selectedBuildingBbox]);

  // Deduped org-row lists shared by the collapsed cell summaries and the
  // expanded OrgSection pickers, so the headline confidence + runners-up peek
  // shown when collapsed match the table shown when opened (surfaces agree).
  // Landlord = filtered HMLR deeds + agentic candidates; tenant = tenant
  // matches; owner-occupier merges all three (the owner may be either).
  const tenantOrgRows = useMemo(
    () => dedupeOrgRows(tenantMatches.map(tenantToOrgRow)),
    [tenantMatches]
  );
  const landlordOrgRows = useMemo(
    () =>
      dedupeOrgRows([
        ...filteredHmlrMatches.map(hmlrToOrgRow),
        ...(agenticMatches ?? []).map(agenticToOrgRow),
      ]),
    [filteredHmlrMatches, agenticMatches]
  );
  const ownerOrgRows = useMemo(
    () =>
      dedupeOrgRows([
        ...filteredHmlrMatches.map(hmlrToOrgRow),
        ...(agenticMatches ?? []).map(agenticToOrgRow),
        ...tenantMatches.map(tenantToOrgRow),
      ]),
    [filteredHmlrMatches, agenticMatches, tenantMatches]
  );

  // The Contacts cells below this block read the PERSISTED landlord/tenant decision
  // (that is what the discovery worker searches against), while the Ownership cells
  // hold the operator's pick in local state until Save. So an unsaved change left the
  // two disagreeing - the Ownership cell naming the new company while the Contacts
  // cell below still showed, and kept discovering, contacts for the old one. Persist
  // an org pick as soon as it is made, through `onSaveOrgDecision`, so they can't
  // drift. Null on /leads/search, where the equivalent save writes a VOA *review* -
  // stamping reviewedAt/reviewedBy and flipping the row New -> Reviewed, which is not
  // something an org click should do.
  //
  // Written straight away rather than debounced. A debounce here would only collapse
  // a double-click - which the equality guard below already refuses - while leaving a
  // window in which a pick is applied on screen but not yet written, and EVERY path
  // that resets or unmounts the panel (Skip, discard-and-leave, delete, a subject
  // change, an external rewrite) would then have to decide whether to flush it or
  // drop it. One forgotten path is a silently lost or a silently resurrected
  // decision, so there is no window.
  // A COUNT of writes in flight, not a flag: picks fire one write each, so a boolean's
  // first completion would clear it while a later write was still running and
  // re-enable Save / Promote / Create site underneath it.
  const [orgSavesInFlight, setOrgSavesInFlight] = useState(0);
  const orgSaving = orgSavesInFlight > 0;
  // The last org values this panel DISPATCHED a write for, per role - the yardstick for
  // "is there anything left to write", and nothing else. Deliberately a ref, and
  // deliberately not wired into `dirty` or the reset paths: those already have a
  // three-way relationship between the props, the FE's default pick and the displayed
  // selection, and adding a fourth notion of "current" to it is how this panel gets a
  // stuck "You have unsaved changes" prompt. The cost of leaving it out is that a
  // just-written pick reads as unsaved for the length of one refetch, which is what an
  // unsaved pick did anyway.
  //
  // A ref, not state, because two picks in the same tick must each see what the one
  // before it dispatched. Recorded at DISPATCH, so re-picking the previous org while a
  // write is still in flight is compared against the pick being written (and so writes)
  // rather than against the stored value (which would call it a no-op and leave the
  // abandoned pick in the database). Rolled back per role if the write fails.
  const lastWrittenOrgsRef = useRef<Partial<OrgSelection> | null>(null);

  // Apply an org change and persist just that change. Every explicit operator action
  // on the Ownership cells goes through here; the auto-clear effects deliberately do
  // not - those are derived corrections, not decisions.
  const commitOrgSelection = (orgs: OrgSelection, patch: OrgDecisionPatch) => {
    // The display always follows the click - a pick the panel is not showing (the
    // auto-cleared landlord, say) has to move the selection even when it happens to
    // match what is stored, or the click is a dead one.
    setLandlord(orgs.landlord);
    setTenant(orgs.tenant);
    setOwnerOccupier(orgs.ownerOccupier);
    if (!onSaveOrgDecision) return;
    // A displayed null is not always a decision: a saved landlord whose deed doesn't
    // overlap the chosen building is auto-cleared for DISPLAY on mount (see the
    // auto-validate effect). Writing that null - which ticking Owner-occupier, or
    // picking the other role, would otherwise do - deletes a decision the operator
    // never touched. Only a null they actually chose reaches the BE, and a deliberate
    // Clear is unaffected: it is only reachable while the role is non-null, so it is
    // never an auto-clear.
    const decided: OrgDecisionPatch = { ...patch };
    if (patch.landlord === null && wasLandlordAutoCleared)
      delete decided.landlord;
    if (patch.tenant === null && wasTenantAutoCleared) delete decided.tenant;
    if (Object.keys(decided).length === 0) return;
    // Whether there is anything to WRITE is judged over the keys this PATCH actually
    // carries, against what is STORED (or being stored) - never against what was
    // displayed, and never over a role the patch leaves alone. Both halves matter:
    // clicking the FE's default pick to affirm it has to write (the display already
    // shows that org, so a display comparison would call it a no-op and leave exactly
    // the Ownership/Contacts drift this change removes), and a role outside the patch
    // that legitimately differs from stored - an unpersisted default tenant, a
    // mount-time auto-cleared landlord - must not make every re-click of an
    // already-saved org look like a change.
    const written = lastWrittenOrgsRef.current ?? {};
    const storedLandlord =
      "landlord" in written
        ? (written.landlord ?? null)
        : (initialLandlord ?? null);
    const storedTenant =
      "tenant" in written ? (written.tenant ?? null) : (initialTenant ?? null);
    const storedOwnerOccupier = written.ownerOccupier ?? initialOwnerOccupier;
    const unchanged =
      (!("landlord" in decided) ||
        sameOrg(decided.landlord ?? null, storedLandlord)) &&
      (!("tenant" in decided) ||
        sameOrg(decided.tenant ?? null, storedTenant)) &&
      (decided.ownerOccupier === undefined ||
        decided.ownerOccupier === storedOwnerOccupier);
    if (unchanged) return;
    // Recorded from the PATCH actually being sent, not from the displayed selection:
    // a role left out of `decided` keeps whatever the server already holds.
    lastWrittenOrgsRef.current = { ...written, ...decided };
    setOrgSavesInFlight((n) => n + 1);
    onSaveOrgDecision(decided)
      // Reported by the owner (see the prop doc). Here the whole record is dropped, so
      // the guard falls back to the props and picking the same org again is a real
      // retry rather than a no-op against a write that never landed. Wholesale rather
      // than per role: the only cost is that a re-click of some OTHER org written
      // moments earlier re-sends a PATCH the BE treats as a no-op, which is a better
      // trade than bookkeeping that has to stay right through interleaved failures.
      .catch(() => {
        lastWrittenOrgsRef.current = null;
      })
      .finally(() => setOrgSavesInFlight((n) => n - 1));
  };

  // Owner-occupier: a single org is both occupier and owner. The operator
  // picks it in the Tenant block and ticks owner-occupier there. BMA
  // persistence is landlord-centric (both the VOA->Lead promote and
  // create-site read the landlord slot for the OO org; handleSave already
  // mirrors landlord into tenant), so while OO is on we keep landlord and
  // tenant pointed at the same org.
  // Only rendered from the merged owner-occupier cell, so OO is on by construction -
  // and there the one org IS both roles, so both decisions move together.
  const setOwnerOrg = (org: SelectedOrg | null) =>
    commitOrgSelection(
      { landlord: org, tenant: org, ownerOccupier: true },
      // `ownerOccupier` is in the patch even though this cell only renders while it
      // is on: local state saying so is not the same as the row holding it, and a
      // failed toggle write would otherwise leave the server with one org in both
      // slots but the flag off, under a switch still showing on.
      { landlord: org, tenant: org, ownerOccupier: true }
    );
  // The separate (non-owner-occupier) Tenant / Landlord cells: one role each, so the
  // patch names only that role and the other role's saved decision is left alone.
  const selectTenant = (org: SelectedOrg | null) =>
    commitOrgSelection(
      { landlord, tenant: org, ownerOccupier },
      { tenant: org }
    );
  const selectLandlord = (org: SelectedOrg | null) =>
    commitOrgSelection(
      { landlord: org, tenant, ownerOccupier },
      { landlord: org }
    );
  // Landlord captured the moment owner-occupier is toggled ON, so un-ticking
  // restores the operator's prior landlord (which OO temporarily overwrote with
  // the tenant) instead of destroying a pre-existing saved landlord. Null means
  // there's nothing to restore from this session - either OO wasn't
  // operator-toggled (a lead that loaded already saved as OO, so the occupier
  // sits in the persisted landlord slot) or the pre-OO landlord was itself
  // unset - and un-ticking falls back to the HMLR deed default-pick below.
  const preOwnerOccupierLandlordRef = useRef<SelectedOrg | null>(null);
  const handleToggleOwnerOccupier = (v: boolean) => {
    if (v) {
      // Owner-occupier: the tenant/occupier pick becomes the owner too.
      // Remember the landlord we're overwriting so un-ticking can restore it.
      preOwnerOccupierLandlordRef.current = landlord;
      const org = tenant ?? landlord;
      commitOrgSelection(
        { landlord: org, tenant: org, ownerOccupier: true },
        { landlord: org, tenant: org, ownerOccupier: true }
      );
    } else {
      // Un-ticking: the org stays the tenant/occupier; restore the landlord
      // the operator had before OO was toggled on this session (OO had
      // overwritten it with the tenant). With nothing to restore - a lead
      // that loaded already saved as OO, so the occupier is in the persisted
      // landlord slot - fall back to the HMLR deed default-pick for the
      // SELECTED building (`filteredHmlrMatches`, the same list the visible
      // options table renders) so the re-offered owner is guaranteed to be
      // in the current matches - no spurious "not in current matches"
      // warning. If neither exists the field clears.
      const restored =
        preOwnerOccupierLandlordRef.current ??
        topGatedHmlrLandlord(filteredHmlrMatches);
      preOwnerOccupierLandlordRef.current = null;
      commitOrgSelection(
        { landlord: restored, tenant, ownerOccupier: false },
        // The occupier stays the tenant, so the tenant decision is unchanged; what
        // moves is the landlord slot the OO mirror had overwritten.
        { landlord: restored, ownerOccupier: false }
      );
    }
  };

  // The SAVED company each Contacts cell below is about (its discovery ran against
  // this, not against the panel's local pick), used to key those cells.
  const tenantContactsCompany = companyFromDecision(
    peopleDiscovery?.tenantDecision ?? null
  );
  const landlordContactsCompany = companyFromDecision(
    peopleDiscovery?.landlordDecision ?? null
  );

  // Headline "total" confidence shown next to the address in the
  // panel header. Same min-of-three pattern as `leadConfidence` on
  // /leads: weakest of (building polygon match, top filtered HMLR
  // landlord, top tenant). Operator-facing summary, distinct from
  // individual badges shown lower down the panel.
  const totalConfidence = useMemo<number | null>(() => {
    const buildingConf =
      selectedBuilding?.confidence ?? scoredBuildings[0]?.confidence ?? null;
    const landlordConf = filteredHmlrMatches[0]?.confidence ?? null;
    const tenantConf = tenantMatches[0]?.confidence ?? null;
    const values = [buildingConf, landlordConf, tenantConf].filter(
      (v): v is number => v != null
    );
    if (values.length === 0) return null;
    return Math.min(...values);
  }, [selectedBuilding, scoredBuildings, filteredHmlrMatches, tenantMatches]);

  // When the building selection changes, the HMLR filter changes too -
  // a deed that overlapped the prior building may not overlap the new
  // one. Auto-clear the saved landlord pick when it falls outside the
  // current filter so the operator doesn't have to manually un-pick a
  // stale selection. Same idea for tenant: when the source data has
  // been refetched and the saved tenant identity no longer appears in
  // the current matches, clear it. Without this the panel would show
  // a "not in current matches" warning and the left-panel cell would
  // still show the now-invalid pick - confusing on both sides.
  useEffect(() => {
    // Owner-occupier keeps landlord + tenant pointed at the same org, which
    // may be a tenant-sourced pick placed in the landlord slot; the stale-HMLR
    // clear below would wrongly wipe it, so skip the auto-clear while OO is on.
    if (ownerOccupier) return;
    // Platform / manual / agentic selections aren't bound to the HMLR
    // match list: platform/manual are operator-typed or org-list picks,
    // and agentic-research candidates live in the separate `agenticMatches`
    // list (never in `filteredHmlrMatches`). So the "not in filtered
    // matches" check would always fail and silently clear them - for
    // agentic that meant an operator's click set the pick then this effect
    // immediately wiped it (a flicker that never stuck). Skip the auto-
    // clear for all three; it only exists to drop a *stale HMLR deed* pick
    // that no longer polygon-overlaps the chosen building.
    if (
      landlord &&
      landlord.source !== "platform" &&
      landlord.source !== "manual" &&
      landlord.source !== "agentic" &&
      hmlrMatches.length > 0 &&
      !filteredHmlrMatches.some(
        (m) =>
          hmlrToOrgRow(m).asSelectedOrg.displayName === landlord.displayName
      )
    ) {
      setLandlord(null);
    }
    if (
      tenant &&
      tenant.source !== "platform" &&
      tenant.source !== "manual" &&
      tenantMatches.length > 0 &&
      !tenantMatches.some(
        (m) =>
          tenantToOrgRow(m).asSelectedOrg.displayName === tenant.displayName
      )
    ) {
      setTenant(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filteredHmlrMatches, tenantMatches]);

  // Compare against `defaultedInitialBuildingBkey` (which falls back
  // to the top candidate when no review has been saved yet), not
  // `initialBuildingBkey` directly - otherwise the panel would
  // permanently report dirty because the displayed selection
  // legitimately differs from the never-saved-yet null. Same for
  // landlord/tenant: only count it as dirty if the operator explicitly
  // changed the displayed pick.
  //
  // Auto-clears at mount don't count as edits. The auto-validate
  // useEffect above clears `landlord` when the saved deed doesn't
  // polygon-overlap the chosen building (or `tenant` when the saved
  // selection isn't in the current matches list). Without these
  // checks, opening any row with a stale-but-saved landlord would
  // immediately flag the row "unsaved" - even though the operator
  // hasn't touched anything.
  const wasLandlordAutoCleared =
    landlord === null &&
    defaultedInitialLandlord != null &&
    hmlrMatches.length > 0 &&
    !filteredHmlrMatches.some(
      (m) =>
        hmlrToOrgRow(m).asSelectedOrg.displayName ===
        defaultedInitialLandlord.displayName
    );
  const wasTenantAutoCleared =
    tenant === null &&
    defaultedInitialTenant != null &&
    tenantMatches.length > 0 &&
    !tenantMatches.some(
      (m) =>
        tenantToOrgRow(m).asSelectedOrg.displayName ===
        defaultedInitialTenant.displayName
    );
  // Two halves, because two write paths own them. The Ownership fields are written
  // per pick wherever `onSaveOrgDecision` is wired (/leads), so there they are not
  // the autosave's to write: a pick that armed the autosave produced a second PATCH
  // of the whole row a second later, carrying the display-only nulls the per-pick
  // path exists to refuse. Everything else - the building and the PV flag - is only
  // ever written by the autosave (or by Promote on the search page).
  const orgDirty =
    (!wasLandlordAutoCleared &&
      landlord?.displayName !== defaultedInitialLandlord?.displayName) ||
    (!wasTenantAutoCleared &&
      tenant?.displayName !== defaultedInitialTenant?.displayName) ||
    ownerOccupier !== initialOwnerOccupier;
  const otherDirty =
    buildingBkey !== defaultedInitialBuildingBkey ||
    // Excluded when this panel writes the flag itself (the linked-site mode):
    // it is already persisted, and Save is disabled there, so counting it as
    // dirty would raise an "unsaved changes" blocker the operator has no way to
    // clear until the lead refetches.
    (!writesHasPvImmediately && hasPv !== initialHasPv);
  // What the parent's left-table overlay and pending-selection emit read: any
  // displayed value that differs from the props, whichever path will write it.
  const dirty = orgDirty || otherDirty;
  // What arms the autosave. Where picks write themselves, an org change alone
  // does not: one click, one PATCH.
  const perPickWrites = onSaveOrgDecision != null;
  const autosaveDirty = perPickWrites ? otherDirty : dirty;

  // What the autosave writes: every field the panel owns, in one payload.
  const autosavePayload = useMemo<SavePayload>(
    () => ({
      buildingBkey,
      landlord,
      tenant: ownerOccupier ? landlord : tenant,
      ownerOccupier,
      hasPv,
      newPolygonCandidate:
        extraCandidates.find((c) => c.bkey === buildingBkey) ?? null,
    }),
    [buildingBkey, landlord, tenant, ownerOccupier, hasPv, extraCandidates]
  );
  const { saveState, runAutosave, resetSaveState } = useLeadAutosave({
    dirty: autosaveDirty,
    payload: autosavePayload,
    disabled: autosaveDisabled,
    onSave,
    onFlushFailed,
  });

  // Tell the parent when dirty state changes so it can intercept row-
  // switch clicks with a confirm() before swapping the panel subject.
  useEffect(() => {
    onDirtyChange?.(dirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty]);

  // Bubble the panel's *unsaved* selection up to the parent so the
  // left-panel table can reflect mid-edit changes immediately. Always
  // emit the current state - the parent reads `dirty` to decide
  // whether to render an "unsaved" overlay.
  useEffect(() => {
    onPendingSelectionChange?.({
      buildingBkey,
      landlord,
      tenant,
      ownerOccupier,
      hasPv,
      dirty,
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dirty, buildingBkey, landlord, tenant, ownerOccupier, hasPv]);

  // Block in-app navigation only while a save has actually FAILED - autosave
  // covers the ordinary case, so there is nothing to warn about otherwise.
  const blocker = useBlocker(saveState.kind === "failed");
  useEffect(() => {
    if (blocker.state === "blocked") {
      const ok = window.confirm(
        "The last change to this lead could not be saved. Discard it and leave?"
      );
      if (ok) {
        // Discard local edits BEFORE proceeding, AND clear the failure that
        // armed the blocker. Resetting the edits alone leaves `saveState` on
        // "failed", so the gate stays armed forever: the row is now clean, so
        // autosave can never run to clear it, and every subsequent navigation -
        // a sort click, a filter, a keystroke in the search form - re-prompts.
        // flushSync ensures both land before blocker.proceed() runs.
        flushSync(() => {
          setBuildingBkey(defaultedInitialBuildingBkey);
          setLandlord(defaultedInitialLandlord);
          setTenant(defaultedInitialTenant);
          setOwnerOccupier(initialOwnerOccupier);
          setHasPv(initialHasPv);
          setError(null);
          resetSaveState();
        });
        blocker.proceed();
      } else {
        blocker.reset();
      }
    }
  }, [
    blocker,
    defaultedInitialBuildingBkey,
    defaultedInitialLandlord,
    defaultedInitialTenant,
    initialOwnerOccupier,
    initialHasPv,
    resetSaveState,
  ]);

  // Block browser-level navigation while an edit has not reached the server: one
  // the autosave has yet to write, or a pick whose write is still in flight.
  useEffect(() => {
    if (!autosaveDirty && !orgSaving) return;
    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, [autosaveDirty, orgSaving]);

  const [buildingSort, setBuildingSort] = useState<{
    key: "confidence" | "surfaceAreaSqm" | "distanceMetres";
    dir: SortDirection;
  } | null>({ key: "confidence", dir: "desc" });
  const sortedBuildings = useMemo(() => {
    if (!buildingSort) return scoredBuildings;
    const k = buildingSort.key;
    return sortRows(
      scoredBuildings,
      (a, b) => (Number(a[k] ?? 0) - Number(b[k] ?? 0)) as number,
      buildingSort.dir
    );
  }, [scoredBuildings, buildingSort]);

  // Stable per-bkey colour assignment for the multi-candidate case.
  // Keyed off buildingCandidates' original order (which the BE
  // pre-sorts by confidence) so the colour doesn't shuffle when the
  // operator changes the table sort. Null when only one candidate -
  // the Color column hides and the polygon uses the default tint.
  const bkeyToColor = useMemo<Record<string, string> | null>(() => {
    if (buildingCandidates.length <= 1) return null;
    const out: Record<string, string> = {};
    buildingCandidates.forEach((b, i) => {
      out[b.bkey] = buildingColourForIndex(i);
    });
    return out;
  }, [buildingCandidates]);

  // Every action button is held while ANY save is in flight: an explicit Save /
  // Promote, a per-pick org write, or the autosave. They had one flag between them
  // at first, which let the auto-save's completion re-enable the buttons in the
  // middle of an explicit Save / Promote - and a second Promote click there creates
  // a second site. The autosave was then left out, so Promote was clickable while
  // its PATCH was still outstanding and the two could race on the same row.
  const busy = saving || orgSaving || saveState.kind === "saving";
  // Shared with the bulk promote modal on /leads/search, so the two cannot disagree
  // about whether promoting is allowed.
  const promoteGate = useOutreachPromoteGate();

  const handleSave = async (kind: "save" | "promote" | "promoteWithTag") => {
    setSaving(true);
    setError(null);
    try {
      const payload: SavePayload = {
        buildingBkey,
        landlord,
        tenant: ownerOccupier ? landlord : tenant,
        ownerOccupier,
        hasPv,
        // If the picked building was clicked off-list (an OSM building the
        // resolver didn't offer), send its geometry so the BE persists it
        // into polygon_candidates - otherwise acceptedBkey wouldn't resolve.
        newPolygonCandidate:
          extraCandidates.find((c) => c.bkey === buildingBkey) ?? null,
      };
      if (kind === "promote" && onPromote) {
        await onPromote(payload);
      } else if (kind === "promoteWithTag" && onPromoteWithTag) {
        await onPromoteWithTag(payload);
      } else {
        await onSave(payload);
      }
    } catch (e) {
      setError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setSaving(false);
    }
  };

  const handleHasPvChange = async (next: boolean) => {
    setHasPv(next);
    setHasPvSaveError(null);
    // Before a site exists the flag rides the normal dirty + Save flow with the
    // rest of the panel's selections (and on a search result the row isn't a
    // lead yet, so there is nothing to PATCH). Only the read-only linked-site
    // mode needs its own write.
    if (!writesHasPvImmediately || !onSaveHasPv) return;
    setHasPvSaving(true);
    try {
      await onSaveHasPv(next);
    } catch (e) {
      // Roll back rather than leave the switch asserting a value the lead does
      // not hold - the whole point of this control is that what it shows is what
      // the email uses.
      setHasPv(!next);
      setHasPvSaveError(e instanceof Error ? e.message : "Save failed");
    } finally {
      setHasPvSaving(false);
    }
  };

  // The switch itself. Split from the "(set to N kWp)" note below because the
  // linked-site summary lays the two out as separate cells of its grid - see
  // `hasPvKwpNote`.
  const hasPvToggleRow = (
    <div
      style={{
        display: "flex",
        justifyContent: "flex-end",
        alignItems: "center",
        gap: 8,
      }}
    >
      {hasPvSaveError && (
        <span style={{ fontSize: 11.5, color: "#dc3545" }}>
          {hasPvSaveError}
        </span>
      )}
      {hasPvSaving && (
        <span style={{ fontSize: 11.5, color: "#6c757d" }}>Saving...</span>
      )}
      <Form.Check
        type="switch"
        id="has-pv-toggle"
        label={
          <span
            style={{ display: "inline-flex", alignItems: "center", gap: 5 }}
          >
            <SunIcon />
            Has existing PV
          </span>
        }
        checked={effectiveHasPv}
        disabled={derivedHasPv || hasPvSaving}
        title={
          derivedHasPv
            ? `Set from the linked site, which records ${Math.round(
                siteExistingPvKwp ?? 0
              ).toLocaleString()} kWp of existing solar - change it on the site's Demand tab`
            : "This roof already carries a solar PV installation. Used when drafting outreach."
        }
        onChange={(e) => void handleHasPvChange(e.target.checked)}
        style={{ fontSize: 12, margin: 0, minHeight: 0 }}
      />
    </div>
  );

  // The switch is disabled in this branch, so without the figure the row states a
  // fact the operator cannot act on and cannot check. `derivedHasPv` is `kWp > 0`,
  // so there is always a number to print here. Same size as the label it annotates;
  // muted and parenthesised, because it qualifies that label rather than competing
  // with it. The tooltip still says where to change it.
  const hasPvKwpNote = derivedHasPv ? (
    <span style={{ fontSize: 12, color: "#6c757d" }}>
      (set to {Math.round(siteExistingPvKwp ?? 0).toLocaleString()} kWp)
    </span>
  ) : null;

  // Existing-solar flag. One definition, rendered on the Ownership header row
  // wherever that row exists (a search result and a lead) and standalone under the
  // map once a site has been created, which has no section header to share. It
  // previously lived in the Buildings cell header, which put it behind the
  // Location/Buildings branch and inside a block that is null once a site exists,
  // so it was invisible in two of the three modes - hence the single definition
  // and the standalone fallback rather than a copy per branch.
  //
  // Stacks the note under the switch. The linked-site summary does NOT use this -
  // it places the two pieces in its own grid so they occupy a row each.
  //
  // Carries no outer margin: each render site owns its own spacing, because the
  // header row supplies it and the standalone one does not.
  const hasPvControl = (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-end",
        gap: 2,
      }}
    >
      {hasPvToggleRow}
      {hasPvKwpNote}
    </div>
  );

  // What autosave has done with the operator's edits. This is the whole
  // feedback channel now that Save is gone, so the failed state is loud and
  // offers a retry rather than leaving them to discover it on navigation.
  //
  // The linked-site case gets a visible refusal rather than nothing. Before
  // autosave it rendered a DISABLED Save whose tooltip said why; rendering
  // nothing would let an operator change the landlord pick on a lead that
  // already has a site, watch the UI update, and navigate away having
  // persisted nothing - with the panel never saying so.
  const saveIndicator = autosaveDisabled ? (
    <WithTooltip
      text={
        "This lead is already linked to a site, so its picks are read-only " +
        "here. Change them on the site itself."
      }
    >
      <span style={{ fontSize: 11.5, color: "#6c757d", cursor: "help" }}>
        Read-only - linked to a site
      </span>
    </WithTooltip>
  ) : (
    <div
      style={{ fontSize: 11.5, display: "flex", alignItems: "center", gap: 5 }}
    >
      {/* Every state is rendered, including "nothing to save" and "edited, not
          written yet". With no Save button, a blank says nothing about whether
          the edit is safe - and the pending state is the one the operator most
          needs, because the write is a second behind their typing.
          `dirty` is read directly rather than through `saveState`: `saveState`
          only moves once the debounce fires, so keying on it alone left the
          indicator unchanged for the whole window in which the edit was at
          risk. Order matters - a failure outranks everything. Reads the
          autosave's own dirty, not the panel's: where picks write themselves an
          org change is not outstanding, it is in flight (`orgSaving`) or done. */}
      {saveState.kind === "failed" ? (
        <>
          <SaveStatusIcon kind="error" />
          <span style={{ color: "#dc3545" }}>
            Couldn&apos;t save your changes
          </span>
          <Button
            size="sm"
            variant="outline-secondary"
            style={{ fontSize: 11, padding: "1px 6px" }}
            onClick={() => void runAutosave(autosavePayload)}
          >
            Retry
          </Button>
        </>
      ) : saveState.kind === "saving" || orgSaving ? (
        <>
          <SaveStatusIcon kind="pending" />
          <span style={{ color: "#6c757d" }}>Saving...</span>
        </>
      ) : autosaveDirty ? (
        <>
          <SaveStatusIcon kind="pending" />
          <span style={{ color: "#6c757d" }}>
            Changes outstanding, auto-saves
          </span>
        </>
      ) : (
        <>
          <SaveStatusIcon kind="ok" />
          <span style={{ color: "#198754" }}>
            All changes auto-saved
            {saveState.kind === "saved" && (
              <>
                {" · "}
                {saveState.at.toLocaleTimeString("en-GB", {
                  hour: "2-digit",
                  minute: "2-digit",
                })}
              </>
            )}
          </span>
        </>
      )}
    </div>
  );

  // Action button row, rendered both near the top of the panel and at
  // the bottom. The right-panel content can be tall enough that the
  // bottom buttons are well off-screen on first paint; duplicating
  // the row at the top means the actions are always reachable without
  // scrolling regardless of where the operator is in the page.
  // Both renders share the same internal state.
  const actionButtons = (
    <div
      style={{
        display: "flex",
        gap: 8,
        marginTop: 12,
        flexWrap: "wrap",
        alignItems: "center",
      }}
    >
      {linkedSite ? (
        <Button
          variant="success"
          as="a"
          href={linkedSite.siteUrl}
          target="_blank"
          rel="noopener noreferrer"
          title="Open the linked site in a new tab."
        >
          View site
        </Button>
      ) : (
        onCreateSite && (
          <Button
            variant="success"
            disabled={busy || createSiteDisabledReason != null}
            onClick={onCreateSite}
            title={
              createSiteDisabledReason ??
              "Create a site from this lead. Opens the same preview + confirm dialog the bulk Create N sites button uses."
            }
          >
            Create site
          </Button>
        )
      )}
      {onPromote && !isAlreadyLead ? (
        // On /leads/search the per-row commit is "create a Lead from
        // this candidate" - the operator's reviewing VOA rows and
        // each click should promote one. The two buttons split the
        // tag-prompt path off from the default-promote path: most
        // rows the operator just wants to promote; some need a tag
        // attached at create time.
        //
        // Neither advances to the next row: the next thing the operator wants
        // is what they just made, and on a one-row bucket the jump left the
        // panel empty with nothing selected.
        //
        // Hidden once the row IS a Lead, rather than disabled: a solid
        // primary "Promote" still reads as the thing to click even greyed
        // out, and on an already-promoted row the useful action is to go
        // and look at the lead, not to promote it again.
        <>
          <Button
            variant="primary"
            disabled={busy || promoteGate.blocked}
            title={
              promoteGate.blocked
                ? promoteGate.reason
                : "Promote without tagging - applies the panel's current selections and stays on this row."
            }
            onClick={() => handleSave("promote")}
          >
            Promote
          </Button>
          {onPromoteWithTag && (
            <Button
              variant="outline-primary"
              disabled={busy || promoteGate.blocked}
              title={
                promoteGate.blocked
                  ? promoteGate.reason
                  : "Pick tags before promoting. Same selections as Promote, plus the tag picker."
              }
              onClick={() => handleSave("promoteWithTag")}
            >
              Promote with tag
            </Button>
          )}
          {promoteGate.blocked && (
            // Spelled out beside the buttons, not only in a tooltip: a disabled
            // primary button with no reason on screen reads as the page being
            // broken, and a tooltip is not discoverable on the surface where the
            // operator is deciding what to do next.
            <span
              style={{ fontSize: 12, color: "#b26a00", alignSelf: "center" }}
            >
              {promoteGate.reason}
            </span>
          )}
        </>
      ) : null}
      {onPromote && isAlreadyLead && existingLeadId && (
        <Button
          variant="outline-primary"
          as="a"
          href={`/leads?selected=${encodeURIComponent(existingLeadId)}`}
          target="_blank"
          rel="noopener noreferrer"
          title="This row is already a Lead. Open it in the leads list."
        >
          View lead
        </Button>
      )}
      {linkedSite == null &&
        onPremiumEnrich &&
        enrichmentFetchedAt?.premiumEnrichedAt == null && (
          <Button
            variant="outline-primary"
            disabled={busy || premiumEnriching}
            // Mirrors the per-row "Enrich" link inside the
            // Matching-against block: same callback, surfaced in the
            // action row so the operator doesn't have to scroll. No
            // confirm prompt - it's a single row at ~$0.01, the cost
            // is in the label.
            onClick={() => {
              onPremiumEnrich();
            }}
            title="Run Google Places Text Search + Places Nearby for this row (~$0.01 headline rate, free under Google's $200/mo credit at our volume)"
          >
            {premiumEnriching ? "Enriching..." : "Enrich ($0.01)"}
          </Button>
        )}
      {/* After the actions, not before them: the buttons are what the operator
          is reaching for, and a status line in front of them pushed the primary
          action off the left edge. Sits before the `marginLeft: auto` group so
          it trails the actions rather than being flung to the far right. */}
      {saveIndicator}
      {isAlreadyLead ? null : isRejected ? (
        <Button
          variant="outline-secondary"
          disabled={busy}
          // `patch_voa_review` clears `rejectedAt` and stamps `reviewedAt`, so
          // the row flips Rejected -> Viewed. Stays on the current row:
          // operators keep the panel open after un-rejecting to adjust the
          // selections.
          onClick={() => void runAutosave(autosavePayload)}
          style={{ marginLeft: "auto" }}
          title="Clear the rejection. The row becomes Viewed and the panel stays open."
        >
          Revive
        </Button>
      ) : (
        onReject && (
          <Button
            variant="outline-danger"
            disabled={busy}
            onClick={onReject}
            style={{ marginLeft: "auto" }}
            title="Mark this row Rejected. The panel stays open on it, so an accidental click is one Revive away."
          >
            Reject
          </Button>
        )
      )}
      {onRemoveFromLeads && (
        <Button
          variant="outline-danger"
          disabled={busy}
          onClick={onRemoveFromLeads}
          // Pushed all the way right. When onReject is also wired
          // (it isn't on /leads), this still sits to its right
          // because both share marginLeft: auto and the second one
          // takes the residual space.
          style={{ marginLeft: onReject ? undefined : "auto" }}
          title="Delete this lead row. The underlying VOA candidate stays available on /leads/search; only the Lead entry is removed."
        >
          Remove from leads
        </Button>
      )}
    </div>
  );

  // Landlord-research button + AI-trace modal, rendered as the footer of
  // whichever ownership cell holds the landlord (the Landlord cell, or the
  // merged Owner-occupier cell). Null when research isn't wired.
  // Landlord-research controls, composed from small nodes so the button can
  // live either in the collapsed cell footer or inline beside the expanded
  // picker's org-search box (the "Research landlord OR search" row). The
  // AI-trace modal is mounted once at the panel root so it opens from whichever
  // surface the button currently sits on.
  const researchButtonNode = onResearchLandlord ? (
    <>
      <Button
        size="sm"
        variant="outline-primary"
        disabled={researching || researchQueued}
        style={{ flex: "none" }}
        onClick={() => {
          // Open the landlord tile to full width so the result is visible in place
          // when research returns (it lands in the Landlord list below the button).
          // When owner-occupier is on the landlord lives in the merged tenant cell.
          setExpandedCell(ownerOccupier ? "tenant" : "landlord");
          void onResearchLandlord();
        }}
      >
        {researchQueued
          ? "Queued for research..."
          : researching
            ? researchSearchCount != null && researchSearchCount > 0
              ? `Researching landlord... (${researchSearchCount} searches run)`
              : "Researching landlord..."
            : agenticResearchedAt
              ? "Re-research landlord (up to ~$2)"
              : "Research landlord (up to ~$2)"}
      </Button>
      {researchQueued && onCancelQueuedResearch && (
        <Button
          size="sm"
          variant="outline-secondary"
          style={{ flex: "none", marginLeft: 6 }}
          title="Remove this lead from the running research queue. The rest of the queue keeps going; you can research it again later."
          onClick={onCancelQueuedResearch}
        >
          Cancel
        </Button>
      )}
    </>
  ) : null;

  const researchMetaNode =
    onResearchLandlord && agenticResearchedAt ? (
      // The trace link only shows when this run's id is on the row (agenticData /
      // agenticTraceRunId). A row researched before the id was persisted falls back
      // to the plain "Last researched" span.
      researchSubjectRef && researchTraceRunId ? (
        <Button
          size="sm"
          variant="link"
          style={{ fontSize: 11, padding: 0 }}
          title="Open the AI trace for this research run"
          onClick={() => setShowResearchTrace(true)}
        >
          Last researched {new Date(agenticResearchedAt).toLocaleString()}
          {" (view AI trace)"}
        </Button>
      ) : (
        <span style={{ fontSize: 11, color: "#888" }}>
          Last researched {new Date(agenticResearchedAt).toLocaleString()}
        </span>
      )
    ) : null;

  const researchNoResult =
    !researching &&
    !researchQueued &&
    agenticResearchedAt != null &&
    (agenticMatches?.length ?? 0) === 0;

  const researchMessagesNode = (
    <>
      {researching && (
        <div style={{ marginTop: 6, fontSize: 12, color: "#0a58ca" }}>
          Researching the landlord on the open web - this can take a few
          minutes, since it corroborates each candidate across several sources.
          Cited candidates will appear in the Landlord list below when it
          finishes.
        </div>
      )}
      {researchNoResult && (
        <div style={{ marginTop: 6, fontSize: 12, color: "#666" }}>
          Last research found no owner it could confirm from public sources.
          Open the AI trace to see what it checked and why.
        </div>
      )}
    </>
  );

  // Footer shown at the bottom of the ownership cell. `includeButton` is false
  // when the button has moved inline beside the expanded picker's search box,
  // so the footer then carries only the "last researched" meta + messages.
  const makeResearchFooter = (includeButton: boolean): React.ReactNode => {
    if (!onResearchLandlord) return null;
    const showButtonRow = includeButton || researchMetaNode != null;
    if (!showButtonRow && !researching && !researchNoResult) return null;
    return (
      <div
        style={{
          marginTop: 10,
          borderTop: "1px dashed #dee2e6",
          paddingTop: 10,
        }}
      >
        {showButtonRow && (
          <div
            style={{
              display: "flex",
              alignItems: "center",
              gap: 8,
              flexWrap: "wrap",
            }}
          >
            {includeButton && researchButtonNode}
            {researchMetaNode}
          </div>
        )}
        {researchMessagesNode}
      </div>
    );
  };

  const researchTraceModal = onResearchLandlord ? (
    <Modal
      show={showResearchTrace}
      onHide={() => setShowResearchTrace(false)}
      size="xl"
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18 }}>
          Landlord research - AI trace
          {agenticResearchedAt && (
            <div style={{ fontSize: 12, color: "#888", fontWeight: 400 }}>
              Last researched {new Date(agenticResearchedAt).toLocaleString()}
            </div>
          )}
        </Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {researchTrace.isLoading ? (
          <div style={{ fontSize: 12, color: "#888" }}>Loading trace...</div>
        ) : researchTrace.data ? (
          <AITraceViewer trace={researchTrace.data} />
        ) : (
          <div style={{ fontSize: 12, color: "#888" }}>
            No AI trace available for this research run.
          </div>
        )}
      </Modal.Body>
    </Modal>
  ) : null;

  // Is there any landlord-research output to show? Gates the "View research"
  // modal, the tile recommendation strip, the per-row Evidence links, and the
  // related-parties expander.
  const hasResearchOutput =
    !!onResearchLandlord &&
    ((agenticMatches?.length ?? 0) > 0 || !!agenticDecision);

  // The full landlord-research output (R24 decision block + R26 evidence),
  // rendered on demand in a modal (moved off the top of the panel). Opened from
  // the Landlord tile strip and each candidate row's Evidence link.
  const researchResultModal = hasResearchOutput ? (
    <Modal
      show={showResearchModal}
      onHide={() => setShowResearchModal(false)}
      size="lg"
      scrollable
    >
      <Modal.Header closeButton>
        <Modal.Title style={{ fontSize: 18 }}>Landlord research</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <AiResearchResult
          candidates={agenticMatches ?? []}
          decision={agenticDecision}
          researchedAt={agenticResearchedAt}
          focusCandidate={showResearchModal ? researchModalHighlight : null}
          onViewTrace={
            researchSubjectRef && researchTraceRunId
              ? () => {
                  setShowResearchModal(false);
                  setShowResearchTrace(true);
                }
              : undefined
          }
        />
      </Modal.Body>
    </Modal>
  ) : null;

  // Compact recommendation strip shown in the collapsed Landlord tile summary:
  // the verdict chip + the one-line decision + a "View research" link. Only the
  // Landlord (and owner-occupier, which holds the landlord) tile carries it.
  const landlordVerdict = verdictDisplay(agenticDecision?.verdict);
  // Defensive coherence guard (mirrors BE R24): a POSITIVE verdict ("Proceed" /
  // "Proceed with caution") that tells the operator to write to a named owner is
  // only actionable if at least one candidate row backs it - an agentic candidate
  // or a building-filtered HMLR deed. Older runs shipped a green "Proceed ... write
  // to X" decision line over an EMPTY candidate table (X was never a candidate);
  // rendering that strip as actionable is incoherent, so we suppress the positive
  // strip when nothing backs it and let the honest "no owner it could confirm"
  // framing (researchNoResult, already shown when agentic candidates are empty)
  // stand instead. Non-positive verdicts (Not established / Insufficient) keep their
  // strip: their decision line honestly points at the related parties.
  const landlordCandidateCount =
    (agenticMatches?.length ?? 0) + filteredHmlrMatches.length;
  const positiveVerdictLacksCandidate =
    (agenticDecision?.verdict === "PROCEED" ||
      agenticDecision?.verdict === "PROCEED_WITH_CAUTION") &&
    landlordCandidateCount === 0;
  const hasResearchRecommendation =
    hasResearchOutput &&
    !!(agenticDecision?.verdict || agenticDecision?.decisionLine) &&
    !positiveVerdictLacksCandidate;
  // The confidence of the party the decision recommends, pilled under the verdict
  // chip so the operator sees the number without expanding the modal. Same
  // candidate the modal scopes to (recommendedCandidate is shared), so tile and
  // modal never disagree about which party the % belongs to.
  const recommendedLandlord = recommendedCandidate(
    agenticMatches ?? [],
    agenticDecision?.partyToContact
  );
  const recommendedConfidence = recommendedLandlord?.confidence ?? null;
  const landlordRecommendationStrip = hasResearchRecommendation ? (
    <div
      style={{
        display: "flex",
        alignItems: "flex-start",
        gap: 8,
        marginTop: 6,
        paddingTop: 6,
        borderTop: "1px dotted #e9ecef",
        minWidth: 0,
      }}
    >
      {(landlordVerdict.label || recommendedConfidence != null) && (
        <span
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "flex-start",
            gap: 3,
            flex: "none",
            marginTop: 1,
          }}
        >
          {landlordVerdict.label && (
            <span
              style={{
                ...RESEARCH_CHIP_STYLE,
                background: landlordVerdict.colour,
              }}
            >
              {landlordVerdict.label}
            </span>
          )}
          {recommendedConfidence != null && (
            <span style={confPillStyle(recommendedConfidence)}>
              {Math.round(recommendedConfidence)}%
            </span>
          )}
        </span>
      )}
      {agenticDecision?.decisionLine && (
        <span
          style={{
            flex: 1,
            minWidth: 0,
            fontSize: 12,
            color: "#495057",
            whiteSpace: "normal",
            overflowWrap: "anywhere",
            lineHeight: 1.4,
          }}
        >
          {scrubResearchJargon(agenticDecision.decisionLine)}
        </span>
      )}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          openResearchModal(null);
        }}
        style={{
          flex: "none",
          border: "none",
          background: "none",
          padding: 0,
          fontSize: 11.5,
          fontWeight: 600,
          color: "#0d6efd",
          cursor: "pointer",
        }}
      >
        View research
      </button>
    </div>
  ) : null;

  // "Related parties (N)" disclosure, rendered below the Landlord picker table
  // (collapsed by default). This is the single home for related parties -
  // AiResearchResult no longer renders them.
  const relatedParties = agenticDecision?.relatedParties ?? [];
  const relatedPartiesExpander =
    hasResearchOutput && relatedParties.length > 0 ? (
      <div style={{ marginTop: 10 }}>
        <button
          type="button"
          onClick={() => setRelatedPartiesOpen((o) => !o)}
          style={{
            border: "none",
            background: "none",
            padding: 0,
            fontSize: 12,
            fontWeight: 600,
            color: "#0d6efd",
            cursor: "pointer",
          }}
        >
          {relatedPartiesOpen ? "▾" : "▸"} Related parties (
          {relatedParties.length})
        </button>
        {relatedPartiesOpen && (
          <div style={{ marginTop: 6 }}>
            {relatedParties.map((p, i) => (
              <div
                key={`${p.name}:${i}`}
                style={{ fontSize: 12, marginBottom: 4 }}
              >
                <span style={{ fontWeight: 600 }}>{p.name}</span>
                {p.role && (
                  <span style={{ color: "#6c757d" }}> ({p.role})</span>
                )}
                {p.note && (
                  <div style={{ color: "#666", fontSize: 11, marginTop: 1 }}>
                    {p.note}
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    ) : null;

  // Site: Location | Buildings. Extracted so the two placements below render the
  // SAME block rather than a copy that would drift (search puts it above
  // Ownership, a lead puts it at the bottom). Picker-only (!linkedSite), so the
  // read-only linked-site view is unchanged.
  const siteBlock = !linkedSite ? (
    <div style={{ marginTop: 14 }}>
      <div style={GROUP_LABEL_STYLE}>Site</div>
      {selectedBuilding?.matchedExistingSiteID && (
        <Alert variant="warning" style={{ padding: "6px 10px" }}>
          Selected building overlaps existing site{" "}
          <Link to={`/site/${selectedBuilding.matchedExistingSiteID}/edit`}>
            {selectedBuilding.matchedExistingSiteName ??
              selectedBuilding.matchedExistingSiteID}
          </Link>
        </Alert>
      )}
      <div style={GRID_STYLE}>
        {(expandedCell === "buildings"
          ? (["buildings", "location"] as const)
          : (["location", "buildings"] as const)
        ).map((cell) =>
          cell === "location" ? (
            <CollapsibleCell
              key="location"
              title="Location"
              expanded={expandedCell === "location"}
              onToggle={() => toggleCell("location")}
              summary={
                <LocationSummary
                  address={address}
                  attempts={inputContext?.geocodeAttempts ?? []}
                  selected={selectedLocation}
                  mapCentre={mapCentre}
                  expanded={expandedCell === "location"}
                  onViewAll={() =>
                    setMapViewportRequest({ kind: "all-locations" })
                  }
                  showGeocoderSpread={mode === "search"}
                  onEditName={
                    // Address edit / re-enrich is a leads-pane action; on
                    // the search-result triage pane the row isn't a lead
                    // yet, so editing the address there is premature.
                    onReenrichAddress && mode === "lead"
                      ? () => {
                          setAddressDraft(address ?? "");
                          setEditingAddress(true);
                        }
                      : undefined
                  }
                  editing={mode === "lead" && editingAddress}
                  addressDraft={addressDraft}
                  onAddressDraftChange={setAddressDraft}
                  saving={reenriching}
                  onSaveEdit={async () => {
                    try {
                      await onReenrichAddress?.(addressDraft.trim());
                      setEditingAddress(false);
                    } catch (e) {
                      setError(
                        e instanceof Error ? e.message : "Re-enrichment failed"
                      );
                    }
                  }}
                  onCancelEdit={() => {
                    setAddressDraft(address ?? "");
                    setEditingAddress(false);
                  }}
                />
              }
            >
              <LocationSection
                hideHeader
                attempts={inputContext?.geocodeAttempts ?? null}
                selected={selectedLocation}
                onSelect={selectLocation}
                onViewAll={() =>
                  setMapViewportRequest({ kind: "all-locations" })
                }
                onHoverLatLon={setHoveredLocationLatLon}
              />
            </CollapsibleCell>
          ) : (
            <CollapsibleCell
              key="buildings"
              title="Buildings"
              expanded={expandedCell === "buildings"}
              onToggle={() => toggleCell("buildings")}
              onViewAll={() => setMapViewportRequest({ kind: "all-buildings" })}
              summary={
                <BuildingsSummary
                  building={selectedBuilding}
                  colour={
                    selectedBuilding
                      ? (bkeyToColor?.[selectedBuilding.bkey] ?? null)
                      : null
                  }
                  peek={sortedBuildings
                    .filter((b) => b.bkey !== buildingBkey)
                    .slice(0, 4)
                    .map((b) => ({
                      conf: b.confidence,
                      colour: bkeyToColor?.[b.bkey] ?? "#fd7e14",
                    }))}
                  showRunnersUp={showRunnersUp}
                />
              }
            >
              <BuildingTable
                hideHeader
                candidates={sortedBuildings}
                selectedBkey={buildingBkey}
                onSelect={handleSelectBuilding}
                onViewAll={() =>
                  setMapViewportRequest({ kind: "all-buildings" })
                }
                sort={buildingSort}
                setSort={setBuildingSort}
                onHoverBkey={setHoveredBuildingBkey}
                bkeyToColor={bkeyToColor}
              />
              <Form.Check
                type="switch"
                id="show-all-buildings"
                label="Show all available buildings"
                checked={showAllBuildings}
                onChange={(e) => setShowAllBuildings(e.target.checked)}
                style={{ marginTop: 6, fontSize: 13 }}
              />
              {showAllBuildings && (
                <div style={{ fontSize: 12, color: "#666", marginTop: 2 }}>
                  Every OSM building in the map view is outlined in purple -
                  zoom in and click one to pick a building the resolver
                  didn&apos;t offer (it joins the list above and saves with the
                  lead).
                </div>
              )}
            </CollapsibleCell>
          )
        )}
      </div>
    </div>
  ) : null;

  return (
    // No TOP padding, deliberately: a scroll container clips at its PADDING box, so
    // content keeps scrolling up through the top pad and stays visible there. With a
    // `top: 0` sticky header below it that left a 12px strip the map showed through.
    // The header owns that space instead (its paddingTop below), so it covers the
    // scrollport's full top edge. The side padding is safe - the content it insets is
    // inset with it, so nothing scrolls behind it.
    <div style={{ padding: "0 12px 12px", height: "100%", overflowY: "auto" }}>
      {researchTraceModal}
      {researchResultModal}
      {/* Pinned to the panel's scrollport: the address, the flags that qualify
          the row, and the actions all stay reachable while the operator scrolls
          the cells below. z-index clears Leaflet's internal panes (max ~700),
          which sit in this same stacking context. */}
      <div
        style={{
          position: "sticky",
          top: 0,
          zIndex: 1030,
          background: "#fff",
          // 12 of the container's former top padding + the 2 this always had.
          paddingTop: 14,
          paddingBottom: 8,
          marginBottom: 10,
          borderBottom: "1px solid #e9ecef",
        }}
      >
        {onBackToMap && (
          <button
            type="button"
            onClick={onBackToMap}
            style={{
              background: "none",
              border: "none",
              padding: 0,
              marginBottom: 2,
              fontSize: 11.5,
              color: "#0d6efd",
              cursor: "pointer",
              textDecoration: "underline",
            }}
          >
            &lt; Back to map
          </button>
        )}
        <h5
          style={{
            margin: 0,
            fontSize: 15,
            lineHeight: 1.3,
            minWidth: 0,
          }}
        >
          {/* Address edit now lives inline in the Location cell (its "edit"
              link); the header just displays the address. */}
          {address ? formatAddressCasing(address) : "(no address)"}
        </h5>
        {/* Every chip shares ONE wrapping row under the address. Split across
            the title line and a row below, the same facts sat on one line or
            two depending on the surface and the length of the address; a pinned
            header gets at most address + chips. */}
        <div
          style={{
            color: "#666",
            fontSize: 12,
            display: "flex",
            alignItems: "center",
            gap: 5,
            flexWrap: "wrap",
            marginTop: 3,
          }}
        >
          {/* Same badge and precedence as the results table's Status cell -
              shown here too so the operator can tell what they have already
              looked at without scrolling back to the row. */}
          {reviewStatus !== "no-status" && (
            <StatusBadge status={reviewStatus} />
          )}
          {/* Lead value belongs to the RESULT, not the tenant: the tenant
              profile is only one of its inputs, and building size already
              feeds it. Keeping it in the tenant block implied otherwise. */}
          <WithTooltip
            text={leadValueTooltip(leadValue ?? null)}
            maxWidth={340}
          >
            <Badge
              {...leadValueBadgeProps(leadValue ?? null)}
              style={{ fontSize: 10 }}
            >
              {leadValueLabel(leadValue ?? null)} value
            </Badge>
          </WithTooltip>
          {totalConfidence != null && (
            <WithTooltip
              text={
                "Total confidence: minimum of (building polygon match, top " +
                "filtered HMLR landlord, top tenant). Weakest of the three " +
                "is what gates whether the row's worth promoting."
              }
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 400,
                  padding: "1px 6px",
                  borderRadius: 4,
                  background:
                    totalConfidence >= 70
                      ? "#28a745"
                      : totalConfidence >= 40
                        ? "#ffc107"
                        : "#6c757d",
                  color: "#fff",
                  cursor: "help",
                }}
              >
                {Math.round(totalConfidence)}%
              </span>
            </WithTooltip>
          )}
          {/* The Has existing PV state, kept in the pinned header so it stays
                visible however far the operator scrolls. The tooltip names where
                the value came from - the operator's flag or the linked site - and
                how to change it. It deliberately does NOT say where the switch is:
                this branch is reachable in all three modes (a linked site with no
                recorded kWp still leaves `derivedHasPv` false), and the switch sits
                on the Ownership header row in two of them and under the map in the
                third. Deliberately not styled like the confidence badge: it is an
                attribute of the building, not a score. */}
          {effectiveHasPv && (
            <WithTooltip
              text={
                derivedHasPv
                  ? `Existing solar: taken from the linked site, which records ${Math.round(
                      siteExistingPvKwp ?? 0
                    ).toLocaleString()} kWp on its Demand tab. Correct it there, not here.`
                  : "Existing solar: flagged on this lead. Change it with the " +
                    "Has existing PV switch on this panel."
              }
            >
              <span
                style={{
                  fontSize: 11,
                  fontWeight: 400,
                  padding: "1px 6px",
                  borderRadius: 4,
                  background: "#fff3cd",
                  color: "#664d03",
                  border: "1px solid #ffe69c",
                  cursor: "help",
                  whiteSpace: "nowrap",
                }}
              >
                ☀️ Existing solar
              </span>
            </WithTooltip>
          )}
          {leadTags?.map((t) => (
            <Badge
              key={t}
              bg="secondary"
              style={{ fontSize: 10, fontWeight: 400 }}
            >
              {t}
            </Badge>
          ))}
          {proximityContext && <ProximityChip proximity={proximityContext} />}
        </div>
        {duplicateOfAddress !== undefined && duplicateOfAddress !== null && (
          <div style={{ marginTop: 3, fontSize: 11, color: "#6c757d" }}>
            <strong>(D)</strong> Duplicate building - same building as &ldquo;
            {duplicateOfAddress}&rdquo; higher in the results. Promoting both
            makes two leads for one site.
          </div>
        )}
        {actionButtons}
      </div>

      {warnings}

      <LeadPanelMap
        mapCentre={mapCentre}
        viewportRequest={mapViewportRequest}
        onViewportApplied={() => setMapViewportRequest(null)}
        geocodeAttempts={inputContext?.geocodeAttempts ?? []}
        candidates={allCandidates}
        resolvedCandidates={buildingCandidates}
        selectedBkey={buildingBkey}
        hoveredBkey={hoveredBuildingBkey}
        bkeyToColor={bkeyToColor}
        onSelectBuilding={handleSelectBuilding}
        showAllBuildings={showAllBuildings}
        selectedLocation={selectedLocation}
        declaredFloorAreaSqm={inputContext?.declaredFloorAreaSqm ?? null}
        onPickOsmBuilding={handlePickOsmBuilding}
        hoveredHmlrTitleNumber={hoveredHmlrTitleNumber}
        hmlrMatches={hmlrMatches}
        hoveredTenantLatLon={hoveredTenantLatLon}
        hoveredLocationLatLon={hoveredLocationLatLon}
        onSelectLocation={selectLocation}
      />
      {linkedSite ? (
        // Read-only summary mode: the lead has been turned into a
        // site, so the picker sections aren't editable. Show what's
        // already linked below the map and let the operator
        // navigate to the site for any further edits.
        <>
          {/* Three groups the operator reads independently - who occupies the
              building, what the site measures, and the existing-PV flag - so
              they get columns rather than one stack with the toggle floating
              above it. Auto-placement fills row 1 (Tenant, install size,
              toggle) then row 2 (Landlord, area, kWp note), so the toggle sits
              on Tenant's row; `align-items: start` keeps it on that row's top
              edge rather than centred over the cell. The two text columns
              share the space and wrap inside it, so a long tenant name never
              pushes the toggle off the panel. The two text columns read as
              labelled lines and stay left-justified; the toggle column is the
              panel's right edge and justifies to it, so its switch and its
              "(set to N kWp)" line share one right margin.

              The note is its own row-2 cell rather than stacked inside the
              toggle cell: stacked, it made that cell two lines tall while the
              other columns' row-1 cells were one, which pushed row 2 down and
              left a gap beside Landlord and Site building area. As a sibling
              cell it lands on row 2 by the same auto-placement, so all three
              columns are two rows and the gap closes. */}
          <div
            style={{
              marginBottom: 12,
              fontSize: 13,
              display: "grid",
              gridTemplateColumns: "minmax(0, 1fr) minmax(0, 1fr) auto",
              alignItems: "start",
              textAlign: "left",
              columnGap: 20,
              rowGap: 4,
            }}
          >
            {/* The size / footprint / risk chips follow the tenant name here
                too. They are a property of the company, not of the review, so
                creating a site should not lose them - and the name is
                read-only in this mode, so there is nothing for them to sit
                under the way they do in the editable ownership cell. */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                flexWrap: "wrap",
                gap: 6,
              }}
            >
              <span style={{ color: "#666" }}>Tenant:</span>
              {linkedSite.tenantName ?? (
                <span style={{ color: "#aaa" }}>-</span>
              )}
              <TenantCommercialSummary
                tenant={tenantCommercial ?? null}
                layout="inline"
              />
            </div>
            <div>
              <span style={{ color: "#666", marginRight: 6 }}>
                Indicative InRange install size:
              </span>
              {linkedSite.installCapacityKwp != null ? (
                `${Math.round(linkedSite.installCapacityKwp).toLocaleString()} kWp`
              ) : (
                <span style={{ color: "#aaa" }}>-</span>
              )}
            </div>
            <div style={{ justifySelf: "end", textAlign: "right" }}>
              {hasPvToggleRow}
            </div>
            <div>
              <span style={{ color: "#666", marginRight: 6 }}>Landlord:</span>
              {linkedSite.landlordName ?? (
                <span style={{ color: "#aaa" }}>-</span>
              )}
            </div>
            <div>
              <span style={{ color: "#666", marginRight: 6 }}>
                Site building area:
              </span>
              {linkedSite.totalBuildingArea != null ? (
                `${Math.round(linkedSite.totalBuildingArea).toLocaleString()} m²`
              ) : (
                <span style={{ color: "#aaa" }}>-</span>
              )}
            </div>
            {/* Rendered only when there is a figure, so an un-derived flag
                leaves no empty grid cell holding row 2 open. */}
            {hasPvKwpNote && (
              <div style={{ justifySelf: "end", textAlign: "right" }}>
                {hasPvKwpNote}
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          {/* On a search result Site leads the panel: there is no "Matching
              against" block for it to sit under, and the operator picks the
              building before deciding ownership. On a lead it renders at the
              bottom instead (same siteBlock, one definition). */}
          {mode === "search" && siteBlock}
          {/* Ownership: Tenant | Landlord, or a single merged
              Owner-occupier cell spanning the row. The existing-PV toggle shares
              this row rather than floating above it - both are properties of the
              building, and the toggle had no anchor of its own. */}
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: 12,
              marginTop: 14,
              marginBottom: 8,
            }}
          >
            <div style={{ ...GROUP_LABEL_STYLE, margin: "0 2px" }}>
              Ownership
            </div>
            {hasPvControl}
          </div>
          {(() => {
            // People-discovery resolves each role's company to a domain. When
            // the landlord and tenant resolve to the SAME domain they're very
            // likely one company - i.e. owner-occupied - which the operator
            // often only realises at this point. Surface a dismissable nudge
            // (with a one-click set) unless they've already ticked OO. Only
            // possible once both roles have a resolved domain (post-discovery).
            const landlordDomain =
              peopleDiscovery?.contactCandidates?.landlord?.domain
                ?.trim()
                .toLowerCase() || null;
            const tenantDomain =
              peopleDiscovery?.contactCandidates?.tenant?.domain
                ?.trim()
                .toLowerCase() || null;
            const sameDomainSuggestsOO =
              !ownerOccupier &&
              !dismissedSameDomainOOHint &&
              landlordDomain != null &&
              landlordDomain === tenantDomain;
            if (!sameDomainSuggestsOO) return null;
            return (
              <Alert
                variant="info"
                className="d-flex align-items-center gap-2 py-2 px-3"
                style={{ fontSize: 12.5, marginBottom: 8 }}
              >
                <span style={{ flex: 1 }}>
                  Landlord and tenant both resolved to{" "}
                  <strong>{landlordDomain}</strong> - this looks owner-occupied.
                  Use the Owner-occupier switch below if so.
                </span>
                <Button
                  size="sm"
                  variant="link"
                  className="p-0 text-muted"
                  style={{
                    fontSize: 16,
                    lineHeight: 1,
                    textDecoration: "none",
                  }}
                  aria-label="Dismiss"
                  onClick={() => {
                    if (peopleDiscovery?.leadId != null) {
                      rememberOOHintDismissed(peopleDiscovery.leadId);
                    }
                    setDismissedSameDomainOOHint(true);
                  }}
                >
                  &times;
                </Button>
              </Alert>
            );
          })()}
          {ownerOccupier ? (
            <div style={GRID_STYLE}>
              <CollapsibleCell
                span
                title={
                  <>
                    Tenant{" "}
                    <span
                      style={{
                        textTransform: "none",
                        fontWeight: 500,
                        color: "#adb5bd",
                      }}
                    >
                      &middot; also the landlord
                    </span>
                  </>
                }
                expanded={expandedCell === "tenant"}
                onToggle={() => toggleCell("tenant")}
                headerRight={
                  <Form.Check
                    type="switch"
                    id="owner-occupier-toggle"
                    label="Owner-occupier"
                    checked={ownerOccupier}
                    onChange={(e) =>
                      handleToggleOwnerOccupier(e.target.checked)
                    }
                    // Strip the bootstrap form-check block margins/min-height so
                    // the switch centres cleanly against the Change button in
                    // the header's alignItems:center flex row (was sitting off).
                    style={{ fontSize: 12, margin: 0, minHeight: 0 }}
                  />
                }
                footer={makeResearchFooter(
                  !(expandedCell === "tenant" && showPlatformOrg)
                )}
                summary={
                  <>
                    <OrgSummary
                      selected={landlord}
                      rows={ownerOrgRows}
                      confTip={LANDLORD_CONF_TIP}
                      onClear={() => setOwnerOrg(null)}
                      showPlatformOrg={showPlatformOrg}
                      showRunnersUp={showRunnersUp}
                      decisionStrip={landlordRecommendationStrip}
                    />
                  </>
                }
              >
                <OrgSection
                  hideHeader
                  showPlatformOrg={showPlatformOrg}
                  researchControl={
                    expandedCell === "tenant" && showPlatformOrg
                      ? researchButtonNode
                      : undefined
                  }
                  title="Owner-occupier"
                  hmlrMatches={filteredHmlrMatches}
                  tenantMatches={tenantMatches}
                  agenticMatches={agenticMatches}
                  adjudications={agenticDecision?.adjudications}
                  onViewEvidence={openResearchModal}
                  selected={landlord}
                  onSelect={setOwnerOrg}
                  onHoverHmlrTitleNumber={setHoveredHmlrTitleNumber}
                  onHoverTenantLatLon={setHoveredTenantLatLon}
                />
                {relatedPartiesExpander}
              </CollapsibleCell>
            </div>
          ) : (
            <div style={GRID_STYLE}>
              {(expandedCell === "landlord"
                ? (["landlord", "tenant"] as const)
                : (["tenant", "landlord"] as const)
              ).map((cell) =>
                cell === "tenant" ? (
                  <CollapsibleCell
                    key="tenant"
                    title="Tenant"
                    expanded={expandedCell === "tenant"}
                    onToggle={() => toggleCell("tenant")}
                    headerRight={
                      <Form.Check
                        type="switch"
                        id="owner-occupier-toggle"
                        label="Owner-occupier"
                        checked={ownerOccupier}
                        onChange={(e) =>
                          handleToggleOwnerOccupier(e.target.checked)
                        }
                        // Strip the bootstrap form-check block margins/min-height
                        // so the switch centres cleanly against the Change button
                        // in the header's alignItems:center flex row.
                        style={{ fontSize: 12, margin: 0, minHeight: 0 }}
                      />
                    }
                    summary={
                      <>
                        <OrgSummary
                          selected={tenant}
                          rows={tenantOrgRows}
                          confTip={TENANT_CONF_TIP}
                          onClear={() => selectTenant(null)}
                          showPlatformOrg={showPlatformOrg}
                          showRunnersUp={showRunnersUp}
                        />
                        <TenantCommercialSummary
                          tenant={tenantCommercial ?? null}
                          layout="stacked"
                        />
                      </>
                    }
                  >
                    {tenant && (
                      <TenantDomainPanel
                        tenant={tenant}
                        commercial={tenantCommercial ?? null}
                        onChange={(website) =>
                          setTenant({ ...tenant, website })
                        }
                        busy={saving || !!recountingCommercial}
                        onSaveAndRecount={
                          onRecountCommercial &&
                          (async () => {
                            // Save first: the lookup reads the domain off the
                            // PERSISTED decision, so recounting an unsaved
                            // correction would just re-count the old one.
                            await handleSave("save");
                            onRecountCommercial();
                          })
                        }
                      />
                    )}
                    <OrgSection
                      hideHeader
                      showPlatformOrg={showPlatformOrg}
                      title="Tenant"
                      hmlrMatches={[]}
                      tenantMatches={tenantMatches}
                      selected={tenant}
                      onSelect={selectTenant}
                      onHoverTenantLatLon={setHoveredTenantLatLon}
                    />
                  </CollapsibleCell>
                ) : (
                  <CollapsibleCell
                    key="landlord"
                    title="Landlord"
                    expanded={expandedCell === "landlord"}
                    onToggle={() => toggleCell("landlord")}
                    footer={makeResearchFooter(
                      !(expandedCell === "landlord" && showPlatformOrg)
                    )}
                    summary={
                      <>
                        <OrgSummary
                          selected={landlord}
                          rows={landlordOrgRows}
                          confTip={LANDLORD_CONF_TIP}
                          onClear={() => selectLandlord(null)}
                          showPlatformOrg={showPlatformOrg}
                          showRunnersUp={showRunnersUp}
                          decisionStrip={landlordRecommendationStrip}
                        />
                      </>
                    }
                  >
                    <OrgSection
                      hideHeader
                      showPlatformOrg={showPlatformOrg}
                      researchControl={
                        expandedCell === "landlord" && showPlatformOrg
                          ? researchButtonNode
                          : undefined
                      }
                      title="Landlord"
                      hmlrMatches={filteredHmlrMatches}
                      tenantMatches={[]}
                      agenticMatches={agenticMatches}
                      adjudications={agenticDecision?.adjudications}
                      onViewEvidence={openResearchModal}
                      selected={landlord}
                      onSelect={selectLandlord}
                      onHoverHmlrTitleNumber={setHoveredHmlrTitleNumber}
                    />
                    {relatedPartiesExpander}
                  </CollapsibleCell>
                )
              )}
            </div>
          )}
        </>
      )}

      {(peopleDiscovery || mode === "search") && (
        <>
          {/* Each Contacts cell is keyed on the company it belongs to. Everything the
              cell holds locally is about that one company - the discovery run it is
              polling, the people ticked for promotion, the typed domain override - so
              when the operator re-targets a role the cell has to start over rather
              than keep reporting the previous company's run ("Ranking 16 people with
              AI...") under the new name. Keyed on the same string the cell renders
              (companyFromDecision), so the two can't disagree about what changed. */}
          {/* Row 3 - Contacts: the decision-makers found at the landlord &
              tenant company. One CollapsibleCell per role, expanding one at a
              time (full-width) like the Ownership cells above. On the search
              pane a row only carries contacts once it's a promoted lead
              (peopleDiscovery is wired from the looked-up lead); a not-yet-lead
              row shows the "only on a lead" note instead. */}
          <div
            style={{
              ...GROUP_LABEL_STYLE,
              marginTop: 14,
              display: "flex",
              alignItems: "baseline",
              gap: 8,
            }}
          >
            Contacts
            {/* No stage line here. The per-role cells below already carry this lead's
                progress in the place it applies to ("Finding contacts 1 of 1..." on the
                button, the stage in each details box), so a lead-level repeat beside the
                heading was the same fact a third time. */}
          </div>
          {peopleDiscovery ? (
            ownerOccupier ? (
              // Owner-occupier: one org is both landlord and occupier, so the two
              // discovery personas rank the SAME company. Pool them into one full-width
              // cell (poolWithRole, deduped by apolloId) so people the tenant ranking
              // excluded but the landlord ranking kept aren't hidden. Find/Pull still
              // target the tenant role; pooling only merges the landlord persona's people
              // into the display + the done-ticks.
              <>
                <div style={GRID_STYLE}>
                  <ContactsRoleCell
                    key={`contacts-oo|${tenantContactsCompany}|${landlordContactsCompany}`}
                    span
                    poolWithRole="landlord"
                    role="tenant"
                    leadId={peopleDiscovery.leadId}
                    decision={peopleDiscovery.tenantDecision}
                    contactCandidates={peopleDiscovery.contactCandidates}
                    contactEnrichments={peopleDiscovery.contactEnrichments}
                    hubspotMatches={peopleDiscovery.hubspotMatches}
                    expanded={expandedCell === "contacts-tenant"}
                    onToggle={() => toggleCell("contacts-tenant")}
                  />
                </div>
                {/* Sole full-width details cell for the occupier, pooling both personas'
                    pulled contacts (same apolloId set as the cell above). */}
                <div style={{ ...GRID_STYLE, marginTop: 12 }}>
                  <ContactDetailsCell
                    key={`contacts-oo-details|${tenantContactsCompany}|${landlordContactsCompany}`}
                    span
                    poolWithRole="landlord"
                    role="tenant"
                    leadId={peopleDiscovery.leadId}
                    contactEnrichments={peopleDiscovery.contactEnrichments}
                    hubspotMatches={peopleDiscovery.hubspotMatches}
                    pipelineStatus={peopleDiscovery.pipelineStatus}
                    hasCompany={!!tenantContactsCompany}
                    apolloCredits={peopleDiscovery.apolloCredits}
                    outreachContactIds={peopleDiscovery.outreachContactIds}
                    expanded={expandedCell === "contacts-tenant-details"}
                    onToggle={() => toggleCell("contacts-tenant-details")}
                  />
                </div>
              </>
            ) : (
              <>
                <div style={GRID_STYLE}>
                  {/* Tenant left, Landlord right - same column order as the Ownership row
                      above (keys "expanded-first" on landlord so the open cell leads). */}
                  {(expandedCell === "contacts-landlord"
                    ? (["contacts-landlord", "contacts-tenant"] as const)
                    : (["contacts-tenant", "contacts-landlord"] as const)
                  ).map((cell) =>
                    cell === "contacts-landlord" ? (
                      <ContactsRoleCell
                        key={`contacts-landlord|${landlordContactsCompany}`}
                        poolWithRole={null}
                        role="landlord"
                        leadId={peopleDiscovery.leadId}
                        decision={peopleDiscovery.landlordDecision}
                        contactCandidates={peopleDiscovery.contactCandidates}
                        contactEnrichments={peopleDiscovery.contactEnrichments}
                        hubspotMatches={peopleDiscovery.hubspotMatches}
                        expanded={expandedCell === "contacts-landlord"}
                        onToggle={() => toggleCell("contacts-landlord")}
                      />
                    ) : (
                      <ContactsRoleCell
                        key={`contacts-tenant|${tenantContactsCompany}`}
                        poolWithRole={null}
                        role="tenant"
                        leadId={peopleDiscovery.leadId}
                        decision={peopleDiscovery.tenantDecision}
                        contactCandidates={peopleDiscovery.contactCandidates}
                        contactEnrichments={peopleDiscovery.contactEnrichments}
                        hubspotMatches={peopleDiscovery.hubspotMatches}
                        expanded={expandedCell === "contacts-tenant"}
                        onToggle={() => toggleCell("contacts-tenant")}
                      />
                    )
                  )}
                </div>
                {/* Contact details - a SEPARATE tile per role beneath its people tile
                    (tenant under tenant, landlord under landlord), listing the pulled
                    contacts. Its own grid so each details cell aligns under its column and
                    expands independently. */}
                <div style={{ ...GRID_STYLE, marginTop: 12 }}>
                  {(expandedCell === "contacts-landlord-details"
                    ? ([
                        "contacts-landlord-details",
                        "contacts-tenant-details",
                      ] as const)
                    : ([
                        "contacts-tenant-details",
                        "contacts-landlord-details",
                      ] as const)
                  ).map((cell) =>
                    cell === "contacts-landlord-details" ? (
                      <ContactDetailsCell
                        key={`contacts-landlord-details|${landlordContactsCompany}`}
                        poolWithRole={null}
                        role="landlord"
                        leadId={peopleDiscovery.leadId}
                        contactEnrichments={peopleDiscovery.contactEnrichments}
                        hubspotMatches={peopleDiscovery.hubspotMatches}
                        pipelineStatus={peopleDiscovery.pipelineStatus}
                        hasCompany={!!landlordContactsCompany}
                        apolloCredits={peopleDiscovery.apolloCredits}
                        outreachContactIds={peopleDiscovery.outreachContactIds}
                        expanded={expandedCell === "contacts-landlord-details"}
                        onToggle={() => toggleCell("contacts-landlord-details")}
                      />
                    ) : (
                      <ContactDetailsCell
                        key={`contacts-tenant-details|${tenantContactsCompany}`}
                        poolWithRole={null}
                        role="tenant"
                        leadId={peopleDiscovery.leadId}
                        contactEnrichments={peopleDiscovery.contactEnrichments}
                        hubspotMatches={peopleDiscovery.hubspotMatches}
                        pipelineStatus={peopleDiscovery.pipelineStatus}
                        hasCompany={!!tenantContactsCompany}
                        apolloCredits={peopleDiscovery.apolloCredits}
                        outreachContactIds={peopleDiscovery.outreachContactIds}
                        expanded={expandedCell === "contacts-tenant-details"}
                        onToggle={() => toggleCell("contacts-tenant-details")}
                      />
                    )
                  )}
                </div>
              </>
            )
          ) : (
            <div style={{ fontSize: 12.5, color: "#6c757d" }}>
              Contacts are only found once this row is promoted to a lead.
            </div>
          )}
        </>
      )}

      {error && (
        <Alert variant="danger" style={{ marginTop: 8 }}>
          {error}
        </Alert>
      )}

      {actionButtons}

      {inputContext && (
        <div style={{ marginTop: 16 }}>
          <InputContextBlock
            context={inputContext}
            buildingCandidates={buildingCandidates}
            enrichmentFetchedAt={enrichmentFetchedAt}
            enrichmentDegradedSources={enrichmentDegradedSources}
            onRefetch={onRefetch}
            refetching={refetching}
            onPremiumEnrich={onPremiumEnrich}
            premiumEnriching={premiumEnriching}
          />
        </div>
      )}

      {/* Site: Location | Buildings. Moved to the very bottom of the panel (below
          the action buttons and the "Matching against" block) per operator layout
          preference. Picker-only (!linkedSite), so the read-only linked-site view
          is unchanged. */}
      {/* On a lead, Site sits at the very bottom (below the action buttons and
          the "Matching against" block) per operator layout preference. On a
          search result there is no such block to sit under, so it belongs above
          Ownership instead - see siteBlock's other render site. */}
      {mode !== "search" && siteBlock}
    </div>
  );
};

// Reads the source's declared expectations (floor area, building count,
// CSV-import-only badge for Google Address Validation's verdict.
// Granularity colour-coded so PREMISE / SUB_PREMISE reads as a green
// tick and ROUTE / OTHER reads as a yellow / red flag. Inferred or
// unconfirmed components surface inline so the operator can spot
// "Google had to correct your postcode" or "locality unconfirmed"
// without diving into the raw verdict.
const AddressVerdictRow: React.FC<{
  verdict: AddressVerdict;
}> = ({ verdict }) => {
  const granularity = (verdict.validationGranularity ?? "").toUpperCase();
  const colour = (() => {
    switch (granularity) {
      case "PREMISE":
      case "SUB_PREMISE":
        return "#28a745"; // green
      case "ROUTE":
        return "#ffc107"; // yellow
      case "OTHER":
        return "#dc3545"; // red
      default:
        return "#6c757d"; // grey
    }
  })();
  const summaryParts: string[] = [humanizeGranularity(granularity)];
  if (verdict.addressComplete === false) {
    summaryParts.push("address incomplete");
  }
  if (verdict.hasInferredComponents) summaryParts.push("some parts inferred");
  if (verdict.hasUnconfirmedComponents) {
    summaryParts.push("some parts unconfirmed");
  }
  return (
    <div
      style={{
        marginTop: 4,
        paddingTop: 4,
        borderTop: "1px dashed #e1e4e8",
      }}
    >
      <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
        <span style={{ color: "#888", minWidth: 130 }}>Address verdict:</span>
        <span
          style={{
            background: colour,
            color: "#fff",
            padding: "0 6px",
            borderRadius: 3,
            fontSize: 10,
            fontWeight: 500,
          }}
        >
          {summaryParts.join(" · ")}
        </span>
      </div>
      {verdict.formattedAddress && (
        <div style={{ display: "flex", gap: 6, marginTop: 2 }}>
          <span style={{ color: "#888", minWidth: 130 }}>Canonical:</span>
          <span style={{ color: "#444" }}>{verdict.formattedAddress}</span>
        </div>
      )}
      {verdict.inferredComponents.length > 0 && (
        <div style={{ marginTop: 2 }}>
          <span
            style={{ color: "#888", minWidth: 130, display: "inline-block" }}
          >
            Inferred / corrected:
          </span>
          {verdict.inferredComponents.map((c, i) => (
            <span key={i} style={{ marginLeft: 6, color: "#444" }}>
              {humanizeComponentType(c.type)}: <code>{c.value ?? "?"}</code>
              {c.replaced
                ? " (replaced)"
                : c.spellCorrected
                  ? " (spell-corrected)"
                  : " (inferred)"}
              {i < verdict.inferredComponents.length - 1 ? "," : ""}
            </span>
          ))}
        </div>
      )}
      {verdict.unconfirmedComponents.length > 0 && (
        <div style={{ marginTop: 2 }}>
          <span
            style={{ color: "#888", minWidth: 130, display: "inline-block" }}
          >
            Unconfirmed:
          </span>
          {verdict.unconfirmedComponents.map((c, i) => (
            <span key={i} style={{ marginLeft: 6, color: "#444" }}>
              {humanizeComponentType(c.type)}: <code>{c.value ?? "?"}</code> (
              {humanizeConfirmation(c.confirmationLevel)})
              {i < verdict.unconfirmedComponents.length - 1 ? "," : ""}
            </span>
          ))}
        </div>
      )}
    </div>
  );
};

// geocode quality) and shows them above the Buildings table so the
// operator can compare candidate values against the target the matcher
// was aiming at. Highlights the closest candidate by area when there's
// a declared area.
const InputContextBlock: React.FC<{
  context: InputContext;
  buildingCandidates: BuildingCandidate[];
  enrichmentFetchedAt?: {
    geocodedAt: string | null;
    buildingResolvedAt: string | null;
    hmlrEnrichedAt: string | null;
    tenantEnrichedAt: string | null;
    epcEnrichedAt: string | null;
    premiumEnrichedAt: string | null;
  };
  enrichmentDegradedSources?: string[];
  onRefetch?: () => Promise<void>;
  refetching?: boolean;
  onPremiumEnrich?: () => Promise<void>;
  premiumEnriching?: boolean;
}> = ({
  context,
  buildingCandidates,
  enrichmentFetchedAt,
  enrichmentDegradedSources,
  onRefetch,
  refetching,
  onPremiumEnrich,
  premiumEnriching,
}) => {
  const {
    sourceLabel,
    address,
    declaredFloorAreaSqm,
    declaredNumBuildings,
    epcMatches,
  } = context;
  const [showRaw, setShowRaw] = useState(false);
  // Polygon arrays (building outlines, deed boundaries) bloat the
  // raw-data dump with thousands of [lon, lat] pairs that drown the
  // useful debug fields. Default to stripping them; operator can
  // toggle on if they specifically want the geometry.
  const [includePolygons, setIncludePolygons] = useState(false);

  const closestArea =
    declaredFloorAreaSqm != null && buildingCandidates.length > 0
      ? buildingCandidates.reduce<BuildingCandidate | null>((best, c) => {
          if (c.surfaceAreaSqm == null) return best;
          if (best == null) return c;
          const bestDelta = Math.abs(
            (best.surfaceAreaSqm ?? 0) - declaredFloorAreaSqm
          );
          const cDelta = Math.abs(c.surfaceAreaSqm - declaredFloorAreaSqm);
          return cDelta < bestDelta ? c : best;
        }, null)
      : null;

  // Pick the EPC cert with the floor area closest to the VOA-declared
  // value when available - that's almost always the cert for this row's
  // unit in a multi-let postcode. Falls back to the most-recent cert
  // (epcMatches arrives sorted by inspection date desc from the BE).
  const bestEpc: EpcMatch | null = (() => {
    const matches = epcMatches ?? [];
    if (matches.length === 0) return null;
    if (declaredFloorAreaSqm == null) return matches[0];
    let best: EpcMatch | null = null;
    let bestDelta = Number.POSITIVE_INFINITY;
    for (const m of matches) {
      if (m.totalFloorAreaSqm == null) continue;
      const delta = Math.abs(m.totalFloorAreaSqm - declaredFloorAreaSqm);
      if (delta < bestDelta) {
        bestDelta = delta;
        best = m;
      }
    }
    return best ?? matches[0];
  })();

  // "Matching against" describes the BASE record we're trying to
  // identify (VOA / CSV row) - not the data we matched against it.
  // Floor area is the one cross-source corroboration we render here
  // because it's a direct fact-check of a base-record value (VOA
  // declared vs EPC measured); other matched data lives in its own
  // sections (HMLR landlord, CH/OSM/Places tenants, EPC certificate
  // block below this panel).
  const items: { label: string; value: string }[] = [
    { label: "Source", value: sourceLabel },
  ];
  if (address) {
    items.push({ label: "Address", value: address });
  }
  if (declaredFloorAreaSqm != null) {
    const closestNote = closestArea?.surfaceAreaSqm
      ? ` (closest building: ${Math.round(closestArea.surfaceAreaSqm).toLocaleString()} m²)`
      : "";
    // EPC corroboration when we have a cert with a floor-area reading.
    // Show absolute delta rather than %, operators reason about
    // "100 m² off" more readily than "12% off".
    let epcNote = "";
    if (bestEpc?.totalFloorAreaSqm != null) {
      const deltaSqm = Math.round(
        Math.abs(bestEpc.totalFloorAreaSqm - declaredFloorAreaSqm)
      );
      epcNote = ` · EPC: ${Math.round(bestEpc.totalFloorAreaSqm).toLocaleString()} m² (Δ${deltaSqm} m²)`;
    }
    items.push({
      label: "Declared floor area",
      value: `${Math.round(declaredFloorAreaSqm).toLocaleString()} m²${closestNote}${epcNote}`,
    });
  }
  if (declaredNumBuildings != null) {
    items.push({
      label: "Declared buildings",
      value: String(declaredNumBuildings),
    });
  }

  // Separate EPC certificate block. Matched-data (not match inputs)
  // so it sits beneath "Matching against" rather than inside it.
  // Only the fields the lead pipeline actually uses are surfaced -
  // total floor area (for the matcher's footprint cross-check) and
  // property type (to confirm it's solar-suitable space rather than
  // e.g. a back office). Energy rating + inspection date are
  // intentionally omitted since they don't feed any lead-pipeline
  // decision.
  const epcItems: { label: string; value: string }[] = [];
  if (bestEpc) {
    if (bestEpc.totalFloorAreaSqm != null) {
      epcItems.push({
        label: "Total floor area",
        value: `${Math.round(bestEpc.totalFloorAreaSqm).toLocaleString()} m²`,
      });
    }
    if (bestEpc.propertyType) {
      epcItems.push({ label: "Property type", value: bestEpc.propertyType });
    }
  }

  return (
    <>
      <div
        style={{
          background: "#f6f8fa",
          border: "1px solid #e1e4e8",
          borderRadius: 4,
          padding: "6px 10px",
          marginBottom: 8,
          fontSize: 12,
        }}
      >
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "baseline",
            marginBottom: 2,
          }}
        >
          <span style={{ color: "#666", fontSize: 11 }}>Matching against</span>
          {context.rawSource && (
            <Button
              size="sm"
              variant="link"
              style={{ fontSize: 11, padding: 0 }}
              onClick={() => setShowRaw(true)}
            >
              View raw source data
            </Button>
          )}
        </div>
        {items.map((it) => (
          <div key={it.label} style={{ display: "flex", gap: 6 }}>
            <span style={{ color: "#888", minWidth: 130 }}>{it.label}:</span>
            <span>{it.value}</span>
          </div>
        ))}
        {context.addressVerdict && (
          <AddressVerdictRow verdict={context.addressVerdict} />
        )}
        {epcItems.length > 0 && (
          <div
            style={{
              marginTop: 6,
              paddingTop: 6,
              borderTop: "1px solid #e1e4e8",
            }}
          >
            <div style={{ color: "#666", fontSize: 11, marginBottom: 2 }}>
              EPC certificate (best-match at this postcode)
            </div>
            {epcItems.map((it) => (
              <div key={it.label} style={{ display: "flex", gap: 6 }}>
                <span style={{ color: "#888", minWidth: 130 }}>
                  {it.label}:
                </span>
                <span>{it.value}</span>
              </div>
            ))}
          </div>
        )}
        {enrichmentFetchedAt && (
          <div
            style={{
              marginTop: 6,
              paddingTop: 6,
              borderTop: "1px solid #e1e4e8",
              color: "#666",
              fontSize: 11,
            }}
          >
            <div style={{ display: "flex", justifyContent: "space-between" }}>
              <span style={{ color: "#888" }}>Data fetched</span>
              <div style={{ display: "flex", gap: 8 }}>
                {onPremiumEnrich &&
                  enrichmentFetchedAt.premiumEnrichedAt == null && (
                    <Button
                      size="sm"
                      variant="link"
                      style={{ fontSize: 11, padding: 0 }}
                      // Single-row premium enrich runs immediately -
                      // no confirm. The cost is in the label, and
                      // matches the auto-confirm behaviour of the
                      // bulk button for sub-10s ETAs.
                      onClick={() => {
                        onPremiumEnrich();
                      }}
                      disabled={premiumEnriching}
                      title="Run Google Places Text Search + Places Nearby for this row (~$0.01 headline rate, free under Google's $200/mo credit at our volume)"
                    >
                      {premiumEnriching ? "Enriching..." : "Enrich ($0.01)"}
                    </Button>
                  )}
                {onRefetch && (
                  <Button
                    size="sm"
                    variant="link"
                    style={{ fontSize: 11, padding: 0 }}
                    onClick={() => {
                      // Re-runs every basic-pass lookup and, if the row
                      // already has premium data, the Google Places
                      // passes too. Copy mirrors the search-confirm +
                      // bulk-premium modals for consistency.
                      const wasPremium =
                        enrichmentFetchedAt.premiumEnrichedAt != null;
                      const tierLine = wasPremium
                        ? "This re-runs basic + premium enrichment for this row."
                        : "This re-runs basic enrichment for this row.";
                      const basicLine =
                        `Basic enrichment (${BASIC_ENRICHMENT_SERVICES}) ` +
                        "relies on free data sources plus Mapbox at ~$0.00075/row. " +
                        "The first 100,000 Mapbox requests/month are free.";
                      const premiumLine = wasPremium
                        ? `\n\nPremium enrichment (${PREMIUM_ENRICHMENT_SERVICES}) ` +
                          "costs ~$0.01/row. Google Maps Platform applies a shared " +
                          "$200/month free credit which covers ~20,000 rows of Places calls."
                        : "";
                      const ok = window.confirm(
                        "Re-fetch this row from upstream sources?\n\n" +
                          `${tierLine}\n\n${basicLine}${premiumLine}\n\n` +
                          "Use this when the cached data looks wrong; " +
                          "skip it if you just want to re-review what's already on screen."
                      );
                      if (ok) onRefetch();
                    }}
                    disabled={refetching}
                    title="Bypass cache and re-fetch every source for this row"
                  >
                    {refetching ? "Refetching..." : "Refetch this row"}
                  </Button>
                )}
              </div>
            </div>
            {(() => {
              // Which BE-side degraded-source keys map to which row in
              // the panel. Lets us tag "(failed)" against the right
              // line so the operator sees specifically what's missing
              // rather than a generic "data degraded" header. Add new
              // BE source keys here as more adaptors get breaker-wired.
              const failedByLabel: Record<string, string[]> = {};
              const tenantSources: string[] = [];
              for (const src of enrichmentDegradedSources ?? []) {
                if (src === "companies_house")
                  tenantSources.push("Companies House");
                else if (src === "overpass")
                  tenantSources.push("OpenStreetMap");
                // Unknown sources fall through silently rather than
                // mis-tagging a row.
              }
              if (tenantSources.length > 0) {
                const tenantLabel = enrichmentFetchedAt.premiumEnrichedAt
                  ? "Tenants (CH + OSM + Places)"
                  : "Tenants (CH + OSM)";
                failedByLabel[tenantLabel] = tenantSources;
              }
              return (
                [
                  ["Geocode (Mapbox)", enrichmentFetchedAt.geocodedAt],
                  ["Buildings", enrichmentFetchedAt.buildingResolvedAt],
                  ["HMLR deeds", enrichmentFetchedAt.hmlrEnrichedAt],
                  [
                    enrichmentFetchedAt.premiumEnrichedAt
                      ? "Tenants (CH + OSM + Places)"
                      : "Tenants (CH + OSM)",
                    enrichmentFetchedAt.tenantEnrichedAt,
                  ],
                  // EPC register intentionally omitted: enrichment is
                  // currently disabled (commit 7f66e044 - 1 req/s rate
                  // limit was the dominant search bottleneck and the
                  // floor-area cross-check value didn't justify it).
                  // Re-add this row if EPC enrichment is re-enabled.
                  ...(enrichmentFetchedAt.premiumEnrichedAt
                    ? ([
                        [
                          "Paid APIs (Places)",
                          enrichmentFetchedAt.premiumEnrichedAt,
                        ],
                      ] as [string, string | null][])
                    : []),
                ] as [string, string | null][]
              ).map(([label, ts]) => {
                const failedSources = failedByLabel[label];
                return (
                  <div
                    key={label}
                    style={{ display: "flex", gap: 6, fontSize: 11 }}
                  >
                    <span style={{ color: "#999", minWidth: 130 }}>
                      {label}:
                    </span>
                    <span>{ts ? formatRelativeTime(ts) : "never"}</span>
                    {failedSources && (
                      <span
                        style={{ color: "#b8860b" }}
                        title={`Upstream rate-limited or unavailable last time this row was enriched - missing: ${failedSources.join(", ")}. Use the Refetch this row button (or the bulk banner above the search results) to retry.`}
                      >
                        ({failedSources.join(" + ")} failed)
                      </span>
                    )}
                  </div>
                );
              });
            })()}
          </div>
        )}
      </div>
      <Modal show={showRaw} onHide={() => setShowRaw(false)} size="lg">
        <Modal.Header closeButton>
          <Modal.Title style={{ fontSize: 16 }}>
            Raw source data - {sourceLabel}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <Form.Check
            type="switch"
            id="raw-data-include-polygons"
            label="Include polygons"
            checked={includePolygons}
            onChange={(e) => setIncludePolygons(e.target.checked)}
            style={{ marginBottom: 8, fontSize: 12 }}
          />
          <pre
            style={{
              fontSize: 11,
              background: "#f8f9fa",
              padding: 8,
              borderRadius: 4,
              maxHeight: "60vh",
              overflow: "auto",
              whiteSpace: "pre-wrap",
              wordBreak: "break-word",
            }}
          >
            {JSON.stringify(
              stripPolygonsForDisplay(context.rawSource ?? {}, includePolygons),
              null,
              2
            )}
          </pre>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant="outline-secondary"
            size="sm"
            onClick={() =>
              navigator.clipboard.writeText(
                JSON.stringify(
                  stripPolygonsForDisplay(
                    context.rawSource ?? {},
                    includePolygons
                  ),
                  null,
                  2
                )
              )
            }
          >
            Copy JSON
          </Button>
          <Button
            variant="secondary"
            size="sm"
            onClick={() => setShowRaw(false)}
          >
            Close
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

// Top-of-panel debug section for the Address -> Location mapping.
// Lists every geocoder that returned a coordinate; clicking one
// switches the operator's selected location, which reactively rescores
// the building options table (distance + confidence) and re-pins the
// map. The backend pre-fetched buildings + HMLR against every anchor
// at search time, so switching is a local re-render with no round
// trip.
const LocationSection: React.FC<{
  attempts: GeocodeAttempt[] | null;
  selected: [number, number] | null;
  onSelect: (loc: [number, number]) => void;
  onViewAll: () => void;
  // Hover handler so the parent can highlight the matching geocoder
  // pin on the map. Argument is null on mouseleave.
  onHoverLatLon?: (latLon: [number, number] | null) => void;
  // Suppress the section's own header (title + "View all") when it's
  // embedded inside a CollapsibleCell that provides them.
  hideHeader?: boolean;
}> = ({
  attempts,
  selected,
  onSelect,
  onViewAll,
  onHoverLatLon,
  hideHeader,
}) => {
  const located = (attempts ?? []).filter(
    (a) => a.lat != null && a.lon != null
  );
  const maxSpread = (() => {
    if (located.length < 2) return null;
    let max = 0;
    for (let i = 0; i < located.length; i++) {
      for (let j = i + 1; j < located.length; j++) {
        const d = haversineMetres(
          located[i].lat!,
          located[i].lon!,
          located[j].lat!,
          located[j].lon!
        );
        if (d > max) max = d;
      }
    }
    return max;
  })();

  return (
    <div style={{ marginBottom: hideHeader ? 0 : 16 }}>
      {!hideHeader && (
        <div
          style={{
            display: "flex",
            alignItems: "baseline",
            justifyContent: "space-between",
            marginBottom: 4,
          }}
        >
          <h6 style={{ margin: 0 }}>
            Location
            {maxSpread != null && (
              <span
                style={{
                  fontSize: 11,
                  marginLeft: 8,
                  color:
                    maxSpread < 25
                      ? "#28a745"
                      : maxSpread < 100
                        ? "#ffa726"
                        : "#d9534f",
                }}
              >
                geocoders disagree by {Math.round(maxSpread)} m
              </span>
            )}
          </h6>
          {located.length > 0 && (
            <Button
              size="sm"
              variant="link"
              style={{ fontSize: 11, padding: 0 }}
              onClick={onViewAll}
            >
              View all
            </Button>
          )}
        </div>
      )}
      <div style={{ maxHeight: 320, overflowY: "auto" }}>
        <Table size="sm" hover style={{ marginBottom: 0, fontSize: 12 }}>
          <thead>
            <tr>
              <th style={{ width: 28 }}></th>
              <th>Source</th>
              <th>Confidence</th>
              <th>Lat / Lon</th>
            </tr>
          </thead>
          <tbody>
            {located.map((a, i) => {
              const isSelected =
                selected != null &&
                a.lat === selected[0] &&
                a.lon === selected[1];
              return (
                <tr
                  key={`${a.source}-${i}`}
                  onClick={() => onSelect([a.lat!, a.lon!])}
                  className={isSelected ? "table-primary" : undefined}
                  style={{ cursor: "pointer" }}
                >
                  <td
                    // Map-hover effect attached to the leftmost cell
                    // only - hovering elsewhere on the row was firing
                    // map-pan + marker churn while operators were just
                    // skimming the table.
                    onMouseEnter={
                      onHoverLatLon
                        ? () => onHoverLatLon([a.lat!, a.lon!])
                        : undefined
                    }
                    onMouseLeave={
                      onHoverLatLon ? () => onHoverLatLon(null) : undefined
                    }
                  >
                    <Form.Check type="radio" checked={isSelected} readOnly />
                  </td>
                  <td>{a.source}</td>
                  <td>
                    <WithTooltip
                      text={formatGeocoderTooltip({
                        source: a.source,
                        confidenceLabel: a.confidenceLabel ?? null,
                      })}
                    >
                      <span style={{ cursor: "help" }}>
                        {a.confidenceLabel ?? "-"}
                      </span>
                    </WithTooltip>
                  </td>
                  <td style={{ color: "#888" }}>
                    {a.lat!.toFixed(5)}, {a.lon!.toFixed(5)}
                  </td>
                </tr>
              );
            })}
            {located.length === 0 && (
              <tr>
                <td colSpan={4} style={{ color: "#999" }}>
                  No geocoder returned a result. Building + HMLR data may be
                  sparse.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>
    </div>
  );
};

const BuildingTable: React.FC<{
  candidates: BuildingCandidate[];
  selectedBkey: string | null;
  onSelect: (bkey: string) => void;
  onViewAll: () => void;
  sort: {
    key: "confidence" | "surfaceAreaSqm" | "distanceMetres";
    dir: SortDirection;
  } | null;
  setSort: (
    s: {
      key: "confidence" | "surfaceAreaSqm" | "distanceMetres";
      dir: SortDirection;
    } | null
  ) => void;
  // Hover handler so the parent can highlight the matching polygon
  // on the map. bkey is null on mouseleave.
  onHoverBkey?: (bkey: string | null) => void;
  // Per-bkey polygon-outline colour, used for the Color column when
  // there are multiple candidates so the operator can match rows to
  // polygons on the map. Null when there's only one candidate.
  bkeyToColor: Record<string, string> | null;
  // Suppress the section's own header when embedded in a CollapsibleCell.
  hideHeader?: boolean;
}> = ({
  candidates,
  selectedBkey,
  onSelect,
  onViewAll,
  sort,
  setSort,
  onHoverBkey,
  bkeyToColor,
  hideHeader,
}) => (
  <div style={{ marginBottom: hideHeader ? 0 : 16 }}>
    {!hideHeader && (
      <div
        style={{
          display: "flex",
          alignItems: "baseline",
          justifyContent: "space-between",
          marginBottom: 4,
        }}
      >
        <h6 style={{ margin: 0 }}>Buildings</h6>
        {candidates.length > 0 && (
          <Button
            size="sm"
            variant="link"
            style={{ fontSize: 11, padding: 0 }}
            onClick={onViewAll}
          >
            View all
          </Button>
        )}
      </div>
    )}
    <div style={{ maxHeight: 320, overflowY: "auto" }}>
      <Table size="sm" hover striped style={{ marginBottom: 0, fontSize: 12 }}>
        <thead>
          <tr>
            <th style={{ width: 28 }}></th>
            {bkeyToColor && <th style={{ width: 48 }}>Color</th>}
            <SortHeader<"confidence">
              cycle="three-way"
              tooltip="Click to sort"
              label="Confidence"
              sortKey="confidence"
              sort={
                sort && sort.key === "confidence"
                  ? { key: "confidence", dir: sort.dir }
                  : null
              }
              setSort={(s) => setSort(s as typeof sort)}
            />
            <SortHeader<"surfaceAreaSqm">
              cycle="three-way"
              tooltip="Click to sort"
              label="Area (m²)"
              sortKey="surfaceAreaSqm"
              sort={
                sort && sort.key === "surfaceAreaSqm"
                  ? { key: "surfaceAreaSqm", dir: sort.dir }
                  : null
              }
              setSort={(s) => setSort(s as typeof sort)}
            />
            <SortHeader<"distanceMetres">
              cycle="three-way"
              tooltip="Click to sort"
              label="Distance (m)"
              sortKey="distanceMetres"
              sort={
                sort && sort.key === "distanceMetres"
                  ? { key: "distanceMetres", dir: sort.dir }
                  : null
              }
              setSort={(s) => setSort(s as typeof sort)}
            />
          </tr>
        </thead>
        <tbody>
          {candidates.map((b) => {
            const checked = b.bkey === selectedBkey;
            return (
              <tr
                key={b.bkey}
                className={checked ? "table-primary" : undefined}
                style={{ cursor: "pointer" }}
                onClick={() => onSelect(b.bkey)}
              >
                <td
                  onMouseEnter={
                    onHoverBkey ? () => onHoverBkey(b.bkey) : undefined
                  }
                  onMouseLeave={
                    onHoverBkey ? () => onHoverBkey(null) : undefined
                  }
                >
                  <Form.Check
                    type="radio"
                    checked={checked}
                    readOnly
                    aria-label="Select building"
                  />
                </td>
                {bkeyToColor && (
                  <td>
                    <span
                      aria-label={`Polygon colour for building ${b.bkey}`}
                      style={{
                        display: "inline-block",
                        width: 12,
                        height: 12,
                        borderRadius: "50%",
                        background: bkeyToColor[b.bkey] ?? "#fd7e14",
                        border: "1px solid rgba(0,0,0,0.2)",
                        verticalAlign: "middle",
                      }}
                    />
                  </td>
                )}
                <td>
                  <WithTooltip text={formatBuildingMatchTooltip(b)}>
                    <span style={{ cursor: "help" }}>
                      {b.confidence == null
                        ? "-"
                        : `${Math.round(b.confidence)}%`}
                    </span>
                  </WithTooltip>
                </td>
                <td>{b.surfaceAreaSqm?.toFixed(0) ?? "-"}</td>
                <td>{Math.round(b.distanceMetres)}</td>
              </tr>
            );
          })}
          {candidates.length === 0 && (
            <tr>
              <td colSpan={bkeyToColor ? 5 : 4} style={{ color: "#999" }}>
                No building candidates.
              </td>
            </tr>
          )}
        </tbody>
      </Table>
    </div>
  </div>
);

const OrgSection: React.FC<{
  title: string;
  hmlrMatches: HmlrMatch[];
  tenantMatches: TenantMatch[];
  // Agentic landlord-research candidates (landlord section only). Rendered as
  // a distinct source, manually selectable, never auto-picked.
  agenticMatches?: AgenticMatch[];
  // R26 per-candidate adjudications from the research decision (landlord section
  // only). When a row's displayName matches an adjudication name, the row shows
  // the CONFIRMED/CONTRADICTED/... chip + note in place of the raw reasoning.
  adjudications?: AgenticDecision["adjudications"];
  // Opens the "View research" modal scrolled to the named candidate (landlord
  // section only). Drives each agentic/corroborated row's "Evidence" link.
  onViewEvidence?: (candidateName: string) => void;
  selected: SelectedOrg | null;
  onSelect: (org: SelectedOrg | null) => void;
  ownerOccupier?: boolean;
  onToggleOwnerOccupier?: (v: boolean) => void;
  showOwnerOccupierToggle?: boolean;
  disabled?: boolean;
  disabledHint?: string;
  // Optional hover callback for HMLR rows - lets the parent render
  // the deed polygon on the map while the operator's cursor is over
  // the row. Title number is null on mouseleave.
  onHoverHmlrTitleNumber?: (titleNumber: string | null) => void;
  // Optional hover callback for Places/OSM rows that have a lat/lon.
  // Lets the parent render a transient marker on the map (and pan
  // so it's visible). Argument is null on mouseleave.
  onHoverTenantLatLon?: (latLon: [number, number] | null) => void;
  // Suppress the section's own header (title + Clear selection + the
  // owner-occupier toggle) when embedded in a CollapsibleCell that
  // provides the title, a Clear affordance on the summary, and the toggle.
  hideHeader?: boolean;
  // Show the platform-org search box + "use as new org" affordance (leads
  // pane only; premature on the search-result triage pane).
  showPlatformOrg: boolean;
  // Optional control rendered inline to the LEFT of the org-search box (with an
  // "OR" separator) when the picker is expanded - used to place the
  // landlord-research button beside the search box. Ignored when the search box
  // isn't shown (search-pane triage, where showPlatformOrg is false).
  researchControl?: React.ReactNode;
}> = ({
  title,
  hmlrMatches,
  tenantMatches,
  agenticMatches,
  adjudications,
  onViewEvidence,
  selected,
  onSelect,
  ownerOccupier,
  onToggleOwnerOccupier,
  showOwnerOccupierToggle,
  disabled,
  disabledHint,
  onHoverHmlrTitleNumber,
  onHoverTenantLatLon,
  hideHeader,
  showPlatformOrg,
  researchControl,
}) => {
  const [search, setSearch] = useState("");
  const { fetchOrganisations } = useOrganisationList();

  // Filter platform-org search results by org type so the operator
  // doesn't see irrelevant matches. Tenant section -> only Tenant
  // orgs; Landlord section -> only Landlord orgs; Owner-occupier
  // can be either, so it accepts both. Other sections (if any)
  // see the full org list.
  const allowedPlatformOrgTypes = useMemo<Set<string> | null>(() => {
    if (title === "Tenant") return new Set(["Tenant"]);
    if (title === "Landlord") return new Set(["Landlord"]);
    if (title === "Owner-occupier") return new Set(["Tenant", "Landlord"]);
    return null;
  }, [title]);

  const platformRows: OrgRow[] = useMemo(() => {
    const orgs = fetchOrganisations.data?.organisations ?? [];
    const q = search.trim().toLowerCase();
    const searchResults =
      q === ""
        ? []
        : orgs
            .filter((o) => {
              if (
                allowedPlatformOrgTypes &&
                !allowedPlatformOrgTypes.has(o.orgType)
              ) {
                return false;
              }
              return o.name.toLowerCase().includes(q);
            })
            .slice(0, 10)
            .map<OrgRow>((o) => ({
              key: `platform:${o.id}`,
              displayName: o.name,
              source: "platform",
              confidence: null,
              detail: `Platform org${o.orgType ? ` (${o.orgType})` : ""}`,
              orgId: o.id,
              asSelectedOrg: {
                orgId: o.id,
                displayName: o.name,
                source: "platform",
              },
            }));
    // Surface the saved platform / manual selection as a synthetic
    // row even when the search box is empty - otherwise the right
    // panel can't show that a row already has a platform-org pick
    // (the platformRows list only renders search hits, so a saved
    // pick was invisible until the operator typed something).
    // Look up the org's current name from fetchOrganisations when
    // we have it; fall back to the saved displayName otherwise.
    if (
      selected &&
      (selected.source === "platform" || selected.source === "manual") &&
      !searchResults.some(
        (r) =>
          r.orgId != null &&
          selected.orgId != null &&
          r.orgId === selected.orgId
      )
    ) {
      const savedOrg =
        selected.orgId != null
          ? orgs.find((o) => o.id === selected.orgId)
          : null;
      const displayName = savedOrg?.name ?? selected.displayName;
      const orgType = savedOrg?.orgType ?? null;
      const detail =
        selected.source === "manual"
          ? "Manually-typed org (will be created on save)"
          : `Platform org${orgType ? ` (${orgType})` : ""}`;
      const savedRow: OrgRow = {
        key:
          selected.source === "manual"
            ? `manual:${selected.displayName}`
            : `platform:${selected.orgId ?? selected.displayName}`,
        displayName,
        source: selected.source,
        confidence: null,
        detail,
        orgId: selected.orgId ?? null,
        asSelectedOrg: selected,
      };
      return [savedRow, ...searchResults];
    }
    return searchResults;
  }, [fetchOrganisations.data, search, allowedPlatformOrgTypes, selected]);

  const baseRows: OrgRow[] = useMemo(
    () => [
      ...hmlrMatches.map(hmlrToOrgRow),
      ...(agenticMatches ?? []).map(agenticToOrgRow),
      ...tenantMatches.map(tenantToOrgRow),
    ],
    [hmlrMatches, agenticMatches, tenantMatches]
  );

  // Dedupe by display name (case-insensitive), collapsing same-named rows
  // to the higher-confidence one and merging sources. Single-sourced in the
  // module-level `dedupeOrgRows` so the collapsed cell summaries derive the
  // selected row + runners-up peek from exactly this list (surfaces agree).
  const dedupedBase = useMemo(() => dedupeOrgRows(baseRows), [baseRows]);

  const allRows = useMemo(
    // Platform org search matches sit ABOVE the source-side rows -
    // when the operator's typed a search query the platform match is
    // the candidate they're about to pick, so it shouldn't be buried
    // below a long HMLR / Companies House list.
    () => [...platformRows, ...dedupedBase],
    [dedupedBase, platformRows]
  );

  // True when the saved selection corresponds to one of the
  // pre-dedupe match rows (by source-typed id), even if the dedupe
  // merged it under a different row's key. Without this, a lead
  // saved as "OSM Costco" would surface the "no longer in match
  // list" warning whenever a Places "Costco Wholesale" with same
  // displayName appears with higher confidence and wins the dedupe.
  const isSelectedRepresented = useMemo(() => {
    if (!selected) return true;
    if (selected.source === "platform" || selected.source === "manual") {
      return true;
    }
    if (selected.source === "agentic") {
      // A saved agentic pick is "represented" while a same-name agentic
      // candidate is still present (matched on displayName, the only stable
      // id agentic carries). No agentic list -> nothing to contradict it.
      // Since the BE carries a dropped selection over into the fresh list
      // (`_preserve_selected_candidate`), this normally only fires now for a
      // selection whose candidate was never persisted.
      return (
        (agenticMatches ?? []).length === 0 ||
        (agenticMatches ?? []).some(
          (m) =>
            agenticDisplayName(m).toLowerCase() ===
            selected.displayName.toLowerCase()
        )
      );
    }
    if (selected.source === "hmlr") {
      return hmlrMatches.some(
        (m) =>
          (selected.hmlrTitleNumber != null &&
            m.titleNumber === selected.hmlrTitleNumber) ||
          m.proprietorName === selected.displayName
      );
    }
    return tenantMatches.some((m) => {
      if (
        selected.source === "ch" &&
        selected.chCompanyNumber != null &&
        m.companyNumber === selected.chCompanyNumber
      ) {
        return true;
      }
      if (
        selected.source === "google_places" &&
        selected.googlePlaceId != null &&
        m.googlePlaceId === selected.googlePlaceId
      ) {
        return true;
      }
      if (
        selected.source === "osm" &&
        selected.osmId != null &&
        m.osmId === selected.osmId
      ) {
        return true;
      }
      // Fallback: name match, in case the saved selection's source-
      // typed id was rotated by an upstream refetch but the brand
      // name still appears in the matches list.
      return (
        (m.orgName ?? m.companyName).toLowerCase() ===
        selected.displayName.toLowerCase()
      );
    });
  }, [selected, hmlrMatches, tenantMatches, agenticMatches]);

  const [sort, setSort] = useState<{
    key: "displayName" | "confidence";
    dir: SortDirection;
  } | null>({ key: "confidence", dir: "desc" });

  const sortedRows = useMemo(() => {
    // Pin platform-org search matches to the top regardless of sort -
    // when the operator's typed a search query the platform match is
    // the candidate they're about to pick, so it shouldn't slip below
    // a long source-side list when they sort by confidence/name.
    const platform = allRows.filter((r) => r.source === "platform");
    const rest = allRows.filter((r) => r.source !== "platform");
    if (!sort) return [...platform, ...rest];
    const k = sort.key;
    const sortedRest = sortRows(
      rest,
      (a, b) => {
        const av = a[k] ?? "";
        const bv = b[k] ?? "";
        return av < bv ? -1 : av > bv ? 1 : 0;
      },
      sort.dir
    );
    return [...platform, ...sortedRest];
  }, [allRows, sort]);

  // Synthetic key for the saved selection so the radio on the clicked row
  // re-checks correctly. Single-sourced in the module-level `orgSelectedKey`
  // so it can't drift from the collapsed summary's `findOrgRow` lookup.
  const selectedKey = orgSelectedKey(selected);

  return (
    <div
      style={{
        marginBottom: hideHeader ? 0 : 16,
        opacity: disabled ? 0.55 : 1,
      }}
    >
      {!hideHeader && (
        <div
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            marginBottom: 4,
          }}
        >
          <h6 style={{ margin: 0 }}>
            {title}
            {selected && !disabled && (
              // Clear-selection affordance. Visible when a selection
              // exists so the operator can deselect without picking a
              // replacement. The current selection is already shown
              // highlighted in the table below, so we don't repeat it
              // here as a green badge.
              <Button
                size="sm"
                variant="link"
                style={{ fontSize: 11, padding: "0 4px", marginLeft: 8 }}
                onClick={() => onSelect(null)}
              >
                Clear selection
              </Button>
            )}
          </h6>
          {showOwnerOccupierToggle && (
            <Form.Check
              type="switch"
              id={`owner-occupier-${title}`}
              label="Owner-occupier"
              checked={ownerOccupier ?? false}
              onChange={(e) => onToggleOwnerOccupier?.(e.target.checked)}
              style={{ fontSize: 12 }}
            />
          )}
        </div>
      )}
      {disabled && disabledHint && (
        <div style={{ color: "#888", fontSize: 11, marginBottom: 4 }}>
          {disabledHint}
        </div>
      )}
      {/* Visible (not hover-only) warning when the saved selection
          isn't in any of the current match rows. Spells out the
          three resolution paths inline so the operator can act
          without guessing. Uses `isSelectedRepresented` (matches
          by source-typed id against the pre-dedupe lists) rather
          than the synthetic `selectedKey === r.key` check, because
          the dedupe by displayName can collapse two same-named
          matches into one row whose key may not match the saved
          selection's key. */}
      {selected && !disabled && !isSelectedRepresented && (
        <Alert
          variant="warning"
          style={{ fontSize: 12, padding: "6px 10px", marginBottom: 4 }}
        >
          <strong>{selected.displayName}</strong> is no longer in the current
          match list (the underlying source data has changed since this row was
          last reviewed). To resolve:{" "}
          <Button
            size="sm"
            variant="link"
            style={{ padding: 0, fontSize: 12 }}
            onClick={() => onSelect(null)}
          >
            clear the selection
          </Button>
          , click a row in the table to replace it, or refetch the row to
          refresh the source data.
        </Alert>
      )}
      {!disabled && (
        <>
          {/* Typing your own org is a lead-only affordance (`showPlatformOrg`
              is `mode === "lead"`), so on a search result say where it lives
              rather than leaving the operator hunting for a box that is not
              rendered. */}
          {!showPlatformOrg && (
            <div
              style={{
                fontSize: 11,
                color: "#6c757d",
                marginBottom: 4,
              }}
            >
              To enter your own {title.toLowerCase()}, promote this row to a
              lead first.
            </div>
          )}
          {showPlatformOrg && (
            <>
              {researchControl ? (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 8,
                    marginBottom: 4,
                  }}
                >
                  {researchControl}
                  <span
                    style={{
                      fontSize: 12,
                      fontWeight: 600,
                      color: "#6c757d",
                      flex: "none",
                    }}
                  >
                    OR
                  </span>
                  <Form.Control
                    size="sm"
                    placeholder="Select an existing platform org or create a new org"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    style={{ flex: 1, minWidth: 0, marginBottom: 0 }}
                  />
                </div>
              ) : (
                <Form.Control
                  size="sm"
                  placeholder="Select an existing platform org or create a new org"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  style={{ marginBottom: 4 }}
                />
              )}
              {search.trim() &&
                !allRows.some(
                  (r) =>
                    r.displayName.toLowerCase() === search.trim().toLowerCase()
                ) && (
                  <Button
                    size="sm"
                    variant="outline-primary"
                    style={{ marginBottom: 4 }}
                    onClick={() =>
                      onSelect({
                        orgId: uuidv4(),
                        displayName: search.trim(),
                        source: "manual",
                      })
                    }
                  >
                    Use &ldquo;{search.trim()}&rdquo; as a new platform org
                  </Button>
                )}
            </>
          )}
          <div style={{ maxHeight: 320, overflowY: "auto" }}>
            <Table
              size="sm"
              hover
              striped
              style={{ marginBottom: 0, fontSize: 12 }}
            >
              <thead>
                <tr>
                  <th style={{ width: 28 }}></th>
                  <SortHeader<"displayName">
                    cycle="three-way"
                    tooltip="Click to sort"
                    label="Name"
                    sortKey="displayName"
                    sort={
                      sort && sort.key === "displayName"
                        ? { key: "displayName", dir: sort.dir }
                        : null
                    }
                    setSort={(s) => setSort(s as typeof sort)}
                  />
                  <th title="Closest matching existing platform Organisation - fuzzy-matched on the BE. '-' when no match was found.">
                    Platform org
                  </th>
                  <th>Source</th>
                  <SortHeader<"confidence">
                    cycle="three-way"
                    tooltip="Click to sort"
                    label="Conf."
                    sortKey="confidence"
                    sort={
                      sort && sort.key === "confidence"
                        ? { key: "confidence", dir: sort.dir }
                        : null
                    }
                    setSort={(s) => setSort(s as typeof sort)}
                  />
                </tr>
              </thead>
              <tbody>
                {sortedRows.map((r) => {
                  // A deduped row is "checked" when its key matches
                  // the saved selection's synthetic key, OR when one
                  // of the merged-away rows' keys does. Without the
                  // latter, a Costco saved as OSM (key=`osm:way/...`)
                  // wouldn't pre-check its own row if Places "Costco
                  // Wholesale" (key=`places:null`) won the dedupe.
                  const checked =
                    r.key === selectedKey ||
                    (r.mergedKeys?.includes(selectedKey ?? "") ?? false);
                  // Hover handlers per source. HMLR rows render a
                  // deed polygon on the map; Places/OSM rows render
                  // a transient marker + pan the map. Other sources
                  // (platform, manual) have nothing to render so
                  // they get no handlers.
                  // Hover handlers attached to the leftmost cell only
                  // (see LeadSearchPage UX: "map hover effects only
                  // apply in the left most column of each table") so
                  // the cell-typed Pick matches the receiving td.
                  const hover: Pick<
                    React.HTMLAttributes<HTMLTableCellElement>,
                    "onMouseEnter" | "onMouseLeave"
                  > = (() => {
                    if (onHoverHmlrTitleNumber && r.source === "hmlr") {
                      return {
                        onMouseEnter: () =>
                          onHoverHmlrTitleNumber(r.hmlrTitleNumber ?? null),
                        onMouseLeave: () => onHoverHmlrTitleNumber(null),
                      };
                    }
                    if (onHoverTenantLatLon && r.tenantLatLon) {
                      const latLon = r.tenantLatLon;
                      return {
                        onMouseEnter: () => onHoverTenantLatLon(latLon),
                        onMouseLeave: () => onHoverTenantLatLon(null),
                      };
                    }
                    return {};
                  })();
                  return (
                    <tr
                      key={r.key}
                      className={checked ? "table-primary" : undefined}
                      style={{ cursor: "pointer" }}
                      onClick={() => onSelect(r.asSelectedOrg)}
                    >
                      <td {...hover}>
                        <Form.Check type="radio" checked={checked} readOnly />
                      </td>
                      <td>
                        <div style={{ fontWeight: 500 }}>{r.displayName}</div>
                        <div style={{ color: "#888", fontSize: 11 }}>
                          {r.detail}
                        </div>
                        {(() => {
                          // Short, decision-ready "why" line for an AI-research
                          // candidate: the R26 adjudication tag (only when we
                          // reached one) + a ONE-LINE reason. The confidence is
                          // already shown as the coloured % pill in the last
                          // column, so no band word-label here (it duplicated the
                          // verdict). The full reasoning lives behind Evidence -
                          // never dump the essay into the row.
                          const isResearch =
                            r.source === "agentic" || r.agenticCorroborated;
                          if (!isResearch) return null;
                          const adj = adjudications?.find(
                            (a) =>
                              a.name.trim().toLowerCase() ===
                              r.displayName.trim().toLowerCase()
                          );
                          const tag = adj
                            ? adjudicationDisplay(adj.status)
                            : null;
                          const tagLabel = tag?.label
                            ? tag.label.charAt(0).toUpperCase() +
                              tag.label.slice(1)
                            : "";
                          const reason =
                            adj?.note?.trim() || shortReason(r.reasoning);
                          if (!tagLabel && !reason) return null;
                          return (
                            <div
                              style={{
                                marginTop: 3,
                                fontSize: 11.5,
                                color: "#495057",
                                display: "flex",
                                gap: 6,
                                alignItems: "baseline",
                                flexWrap: "wrap",
                              }}
                            >
                              {tag && tagLabel && (
                                <span style={researchTagStyle(tag.colour)}>
                                  {tagLabel}
                                </span>
                              )}
                              {reason && <span>{reason}</span>}
                            </div>
                          );
                        })()}
                        {onViewEvidence &&
                          (r.source === "agentic" || r.agenticCorroborated) &&
                          r.citations &&
                          r.citations.length > 0 && (
                            <button
                              type="button"
                              onClick={(e) => {
                                e.stopPropagation();
                                onViewEvidence(r.displayName);
                              }}
                              style={{
                                marginTop: 2,
                                padding: 0,
                                border: "none",
                                background: "none",
                                fontSize: 11,
                                fontWeight: 600,
                                color: "#0d6efd",
                                cursor: "pointer",
                              }}
                            >
                              Evidence ({r.citations.length})
                            </button>
                          )}
                      </td>
                      <td>
                        {r.platformOrgName ? (
                          r.platformOrgName
                        ) : (
                          <span style={{ color: "#aaa" }}>-</span>
                        )}
                      </td>
                      <td>
                        <Badge bg="secondary" style={{ fontWeight: 400 }}>
                          {r.source}
                        </Badge>
                        {r.agenticCorroborated && (
                          <Badge
                            bg="info"
                            title="An AI research run independently confirmed this owner"
                            style={{ fontWeight: 400, marginLeft: 4 }}
                          >
                            AI-corroborated
                          </Badge>
                        )}
                      </td>
                      <td>
                        <WithTooltip
                          maxWidth={420}
                          text={formatOrgRowTooltip({
                            source: r.source,
                            confidence: r.confidence,
                            detail: r.detail,
                            reasoning: r.reasoning,
                            agenticCorroborated: r.agenticCorroborated,
                          })}
                        >
                          <span style={{ cursor: "help" }}>
                            {r.confidence == null ? (
                              <span style={{ color: "#aaa" }}>-</span>
                            ) : (
                              <span style={confPillStyle(r.confidence)}>
                                {Math.round(r.confidence)}%
                              </span>
                            )}
                          </span>
                        </WithTooltip>
                      </td>
                    </tr>
                  );
                })}
                {sortedRows.length === 0 && (
                  <tr>
                    <td colSpan={5} style={{ color: "#999" }}>
                      {showPlatformOrg
                        ? "No candidates yet. Search above to add a platform org."
                        : "No candidates yet."}
                    </td>
                  </tr>
                )}
              </tbody>
            </Table>
          </div>
        </>
      )}
    </div>
  );
};

export default LeadEditPanel;
